import { Injectable } from '@angular/core';
import { AppConstants } from '../constants/app.contants';

/**
 * Mock Data Service
 * Comprehensive mock data service for all APIs in the project
 */
@Injectable({
  providedIn: 'root'
})
export class MockDataService {

  private readonly demoToken = 'eyJhbGciOiJub25lIiwidHlwIjoiSldUIn0.eyJVc2VySWQiOiJUQ0EtREVNTy0wMDEiLCJGdWxsTmFtZSI6IkFpc2hhIEFsLUt1d2FyaSIsIkVtYWlsQWRkcmVzcyI6ImNvbW1pdHRlZUBkZW1vLnFhIiwiUGhvbmUiOiIrOTc0IDU1NTAgMDEwMCIsIlJvbGUiOiJBZG1pbiIsImV4cCI6NDEwMjQ0NDgwMH0.demo';

  constructor() { }

  /**
   * Get mock data based on URL and method
   */
  getMockData(url: string, method: string, body?: any): any {
    const urlLower = url.toLowerCase();
    const methodUpper = method.toUpperCase();

    // Extract the relevant part of URL (after /api/)
    const apiIndex = urlLower.indexOf('/api/');
    const relevantPath = apiIndex >= 0 ? urlLower.substring(apiIndex + 5) : urlLower;

    console.log('🔍 MockDataService - URL:', url);
    console.log('🔍 Relevant Path:', relevantPath);
    console.log('🔍 Method:', methodUpper);

    // Authentication & Account
    if (relevantPath.includes('account/authenticate') || relevantPath.includes('authenticate')) {
      return this.getAuthenticateResponse();
    }

    if (relevantPath.includes('verify-otp') || relevantPath.includes('verifyotp')) {
      return this.getVerifyOTPResponse();
    }

    if (relevantPath.includes('generateotp')) {
      return this.getGenerateOTPResponse();
    }

    if (relevantPath.includes('validatesession')) {
      return { isSuccess: true, data: true };
    }

    // TCA-specific contracts. These routes intentionally precede the legacy
    // generic mock handlers below, which use Vendor Portal response shapes.
    if (relevantPath.includes('purchaseRequisition/getapprovedrecordbyprnumber'.toLowerCase())) {
      return this.getTcaPurchaseRequisitionDetails(url);
    }

    if (relevantPath.includes('purchaseRequisition/getallapprovedprsfortenderscope'.toLowerCase())) {
      return { isSuccess: true, data: this.getTcaPurchaseRequisitions() };
    }

    if (relevantPath.includes('tenders/gettenderlistdata')) {
      return this.getTcaTenderList(relevantPath);
    }

    if (relevantPath.includes('tenders/gettenderbytenderid')) {
      return this.getTcaTenderDetails(url);
    }

    if (relevantPath.includes('bids/getallbids')) {
      return this.getTcaBids();
    }

    if (relevantPath.includes('bids/getbiddetailsbybidid')) {
      return this.getTcaBidDetails(relevantPath);
    }

    if (relevantPath.includes('bids/getbidsbytendernumber')) {
      return this.getTcaTenderBids();
    }

    if (relevantPath.startsWith('meetings/agenda/') && relevantPath.includes('/unseal-bids')) {
      return this.getTcaUnsealedBids();
    }

    if (relevantPath.startsWith('meetings/agenda/') && relevantPath.includes('/evaluation-reviews')) {
      return this.getTcaEvaluationReviews();
    }

    if (relevantPath.startsWith('meetings/agenda/') && relevantPath.includes('/award-bids')) {
      return this.getTcaAwardBids();
    }

    if (methodUpper === 'GET' && /^meetings\/[^/?]+\/attendance/.test(relevantPath)) {
      return { isSuccess: true, data: this.getTcaAttendance() };
    }

    if (methodUpper === 'GET' && /^meetings\/[^/?]+\/decisions/.test(relevantPath)) {
      return this.getTcaMeetingDecisions(relevantPath);
    }

    if (methodUpper === 'GET' && /^meetings\/[^/?]+(?:\?|$)/.test(relevantPath) && !relevantPath.startsWith('meetings/users')) {
      return this.getTcaMeetingDetails(relevantPath);
    }

    if (methodUpper === 'GET' && relevantPath.startsWith('meetings/users')) {
      return { isSuccess: true, data: this.getTcaMeetingUsers() };
    }

    if (methodUpper === 'GET' && (relevantPath === 'meetings' || relevantPath.startsWith('meetings?'))) {
      return this.getTcaMeetings(relevantPath);
    }

    if (methodUpper === 'GET' && relevantPath.startsWith('agendaitems/getagendaitemdetails/')) {
      return this.getTcaAgendaItemDetails(relevantPath);
    }

    // The read-only agenda viewer uses the REST-style /AgendaItems/{id}
    // endpoint while the edit dialog uses GetAgendaItemDetails/{id}.
    if (methodUpper === 'GET' && /^agendaitems\/[^/?]+(?:\?|$)/.test(relevantPath)) {
      return this.getTcaAgendaItemDetails(relevantPath);
    }

    if (methodUpper === 'GET' && (relevantPath === 'agendaitems' || relevantPath.startsWith('agendaitems?'))) {
      return { isSuccess: true, data: this.getTcaAgendaItems() };
    }

    if (methodUpper === 'GET' && relevantPath.startsWith('purchaserequests')) {
      return { isSuccess: true, data: this.getTcaPurchaseRequisitions() };
    }

    if (methodUpper === 'GET' && relevantPath.startsWith('lookups/')) {
      return this.getTcaLookup(relevantPath);
    }

    if (methodUpper === 'GET' && relevantPath.includes('evaluations/getevaluationdetailsbyid/')) {
      return this.getTcaEvaluationDetails();
    }

    if (methodUpper === 'GET' && relevantPath.includes('evaluations/getevaluationscoringdata/')) {
      return this.getTcaEvaluationScoring();
    }

    if (methodUpper === 'GET' && relevantPath.startsWith('meetings/mom/')) {
      return this.getTcaMinutesDetails();
    }

    if (methodUpper === 'GET' && relevantPath.includes('faqs/getfaqbyid/')) {
      return this.getTcaFaqDetails(relevantPath);
    }

    if (methodUpper === 'GET' && relevantPath.includes('vendor/admin-getvendordetailsbyid')) {
      return this.getTcaVendorDetails(url);
    }

    if (methodUpper === 'GET' && relevantPath.includes('bankapi/admin-getbankaccountsbyvendorid')) {
      return this.getTcaVendorBankAccounts();
    }

    // TCA demo lists. Keep these before the broad tender/vendor fallbacks so
    // every main committee workspace opens with meaningful presentation data.
    if (relevantPath.includes('getallapprovedpr')) {
      return { isSuccess: true, data: [
        { prNumber: 'PR-2026-0142', prName: 'Core Network Modernization', requisitionPurpose: 'Upgrade national fibre backbone capacity', value: 4850000, approvedDate: '2026-08-28', status: 'Approved', isTC: true },
        { prNumber: 'PR-2026-0136', prName: 'Cybersecurity Operations Support', requisitionPurpose: 'Managed SOC and incident response services', value: 1250000, approvedDate: '2026-08-21', status: 'Approved', isTC: true },
        { prNumber: 'PR-2026-0129', prName: 'Office IT Equipment', requisitionPurpose: 'Employee laptop and monitor refresh', value: 178500, approvedDate: '2026-08-12', status: 'Approved', isTC: false }
      ]};
    }

    if (relevantPath.includes('getallvendorsinfo')) {
      return { isSuccess: true, data: [
        { vendorID: 'VND-10021', eRP_VendorID: 'ERP-80451', fullName: 'Gulf Digital Solutions W.L.L.', vendorType: 'Supplier', status: 'Approved', localOrForeign: 'Local', email: 'vendor@demo.qa' },
        { vendorID: 'VND-10034', eRP_VendorID: 'ERP-80509', fullName: 'Doha Network Systems', vendorType: 'Contractor', status: 'Submitted', localOrForeign: 'Local', email: 'bids@dns.qa' },
        { vendorID: 'VND-10048', eRP_VendorID: 'ERP-80612', fullName: 'Nexa Infrastructure Ltd.', vendorType: 'Supplier', status: 'Change Request', localOrForeign: 'Foreign', email: 'tenders@nexa.example' },
        { vendorID: 'VND-10055', eRP_VendorID: '', fullName: 'Pearl Technology Services', vendorType: 'Consultant', status: 'Draft', localOrForeign: 'Local', email: 'contact@pearl.qa' }
      ]};
    }

    if (relevantPath.includes('getallevaluations')) {
      return { isSuccess: true, data: [
        { id: 1, evaluationId: 'EVA-2026-031', prNumber: 'PR-2026-0142', tenderID: 'TND-2026-041', evaluationType: 'Technical', assignedToEn: 'Technical Evaluation Team', status: 'In Progress', score: 78, comments: 'Two of three evaluations completed' },
        { id: 2, evaluationId: 'EVA-2026-028', prNumber: 'PR-2026-0136', tenderID: 'TND-2026-037', evaluationType: 'Commercial', assignedToEn: 'Commercial Evaluation Team', status: 'Pending Review', score: 86, comments: 'Ready for committee review' },
        { id: 3, evaluationId: 'EVA-2026-022', prNumber: 'PR-2026-0118', tenderID: 'TND-2026-030', evaluationType: 'Combined', assignedToEn: 'Tender Committee', status: 'Completed', score: 91, comments: 'Recommended for award' }
      ]};
    }

    if (methodUpper === 'GET' && (relevantPath === 'meetings' || relevantPath.startsWith('meetings?'))) {
      return { isSuccess: true, data: [
        { id: 11, meetingId: 'TCM-2026-018', meetingTitle: 'Tender Committee Meeting 18/2026', dateTime: '2026-09-17T10:00:00', agendaCount: 4, statusName: 'WaitingForAttendance', status: 2, location: 'Bidaya Board Room', chairmanName: 'Eng. Mohammed Al-Marri' },
        { id: 10, meetingId: 'TCM-2026-017', meetingTitle: 'Tender Committee Meeting 17/2026', dateTime: '2026-09-10T09:30:00', agendaCount: 6, statusName: 'Completed', status: 4, location: 'Bidaya Board Room', chairmanName: 'Eng. Mohammed Al-Marri' },
        { id: 12, meetingId: 'TCM-2026-019', meetingTitle: 'Special Tender Review', dateTime: '2026-09-22T11:00:00', agendaCount: 2, statusName: 'Draft', status: 0, location: 'Online', chairmanName: 'Eng. Mohammed Al-Marri' }
      ]};
    }

    if (relevantPath.includes('getallfaqs')) {
      return { isSuccess: true, data: [
        { id: 1, faqId: 'FAQ-2026-001', linkedTenderId: 'TND-2026-041', linkedTenderTitle: 'Core Network Modernization', question: 'Can the bid closing date be extended?', answer: 'Extension requests are reviewed by the Tender Committee and communicated to every invited vendor.', createdBy: 'Omar Al-Nuaimi', createdDate: '2026-09-03T09:15:00Z', status: 'Published' },
        { id: 2, faqId: 'FAQ-2026-002', linkedTenderId: 'TND-2026-044', linkedTenderTitle: 'Managed Cybersecurity Operations', question: 'Is a bid bond mandatory?', answer: 'Yes, where specified in the tender conditions and tender data sheet.', createdBy: 'Aisha Al-Kuwari', createdDate: '2026-09-08T13:40:00Z', status: 'Draft' }
      ]};
    }

    if (relevantPath.includes('admin-getusers')) {
      return { isSuccess: true, data: [
        { id: 'USR-001', fullName: 'Aisha Al-Kuwari', emailAddress: 'committee@demo.qa', role: 'Admin', active: true },
        { id: 'USR-002', fullName: 'Omar Al-Nuaimi', emailAddress: 'secretary@demo.qa', role: 'Secretary', active: true },
        { id: 'USR-003', fullName: 'Sara Ahmed', emailAddress: 'evaluator@demo.qa', role: 'Evaluator', active: true }
      ]};
    }

    // Tender Details - GET specific tender by ID (e.g., /api/tenders/TND001)
    if (methodUpper === 'GET' && relevantPath.includes('tenders/') &&
      !relevantPath.includes('gettenders') &&
      !relevantPath.includes('get-tenders') &&
      !relevantPath.includes('submittender') &&
      !relevantPath.includes('submit') &&
      !relevantPath.includes('getbiddetails') &&
      !relevantPath.includes('bids') &&
      !relevantPath.includes('bid')) {
      // Extract tender ID from URL (e.g., "tenders/TND001" -> "TND001")
      const tenderIdMatch = relevantPath.match(/tenders\/([^\/\?]+)/);
      if (tenderIdMatch && tenderIdMatch[1]) {
        const tenderId = tenderIdMatch[1];
        console.log('✅ Matched: Tender Details GET for ID:', tenderId);
        return this.getTenderDetailsResponse(tenderId);
      }
    }

    // Tenders - Improved matching (case-insensitive)
    if ((relevantPath.includes('tenders/gettenders') ||
      relevantPath.includes('tenders/get-tenders') ||
      relevantPath === 'tenders/gettenders' ||
      (relevantPath.includes('tenders') && methodUpper === 'GET' && !relevantPath.includes('submittender') && !relevantPath.includes('submit'))) &&
      !relevantPath.includes('getbiddetails') &&
      !relevantPath.includes('bids') &&
      !relevantPath.includes('bid')) {
      console.log('✅ Matched: Tenders GET');
      return this.getTendersResponse();
    }

    // Submit Tender (POST)
    if (methodUpper === 'POST' && (relevantPath.includes('tenders/submittender') || relevantPath.includes('submittender'))) {
      return this.getSubmitTenderResponse(body);
    }

    // Bids - Improved matching (case-insensitive)
    if ((relevantPath.includes('bids/getbids') ||
      relevantPath.includes('bids/get-bids') ||
      relevantPath === 'bids/getbids' ||
      (relevantPath.includes('bids') && methodUpper === 'GET' && !relevantPath.includes('getbiddetails') && !relevantPath.includes('submitbid') && !relevantPath.includes('submit'))) &&
      !relevantPath.includes('tenders') &&
      !relevantPath.includes('tender')) {
      console.log('✅ Matched: Bids GET');
      return this.getBidsResponse();
    }

    if (relevantPath.includes('getbiddetails') || relevantPath.includes('bids/getbiddetails')) {
      return this.getBidDetailsResponse();
    }

    // Submit Bid (POST)
    if (methodUpper === 'POST' && (relevantPath.includes('bids/submitbid') || relevantPath.includes('submitbid'))) {
      return this.getSubmitBidResponse(body);
    }

    // Purchase Orders
    if (relevantPath.includes('purchaseorder/getpurchaseorders') || (relevantPath.includes('purchaseorder') && methodUpper === 'GET' && !relevantPath.includes('confirmpo'))) {
      return this.getPurchaseOrdersResponse();
    }

    // Confirm PO (POST/GET)
    if (relevantPath.includes('confirmpo') || relevantPath.includes('confirm')) {
      return this.getConfirmPOResponse();
    }

    // Update/Create Invoice (POST/PUT)
    if ((methodUpper === 'POST' || methodUpper === 'PUT') && (relevantPath.includes('updateorcreateinvoice') || relevantPath.includes('invoice'))) {
      return this.getCreateInvoiceResponse(body);
    }

    // Request GRN (POST)
    if (methodUpper === 'POST' && (relevantPath.includes('requestgrn') || relevantPath.includes('request-grn'))) {
      return this.getRequestGRNResponse(body);
    }

    // Invoices
    if (relevantPath.includes('invoices') || relevantPath.includes('getinvoicespurchaseorders')) {
      return this.getInvoicesResponse();
    }

    // Payments
    if (relevantPath.includes('payments') || relevantPath.includes('getpayments')) {
      return this.getPaymentsResponse();
    }

    // GRNs
    if (relevantPath.includes('grns') || relevantPath.includes('getgrnspurchaseorders')) {
      return this.getGRNsResponse();
    }

    // Send Message (POST)
    if (methodUpper === 'POST' && (relevantPath.includes('messages/sendmessage') || relevantPath.includes('sendmessage'))) {
      return this.getSendMessageResponse(body);
    }

    // Messages (POST for GetMessages)
    if (methodUpper === 'POST' && (relevantPath.includes('messages/getmessages') || relevantPath.includes('getmessages'))) {
      return this.getMessagesResponse();
    }

    // Messages (GET - fallback)
    if (relevantPath.includes('messages/getmessages') || (relevantPath.includes('messages') && methodUpper === 'GET')) {
      return this.getMessagesResponse();
    }

    // Notifications
    if (relevantPath.includes('notificaitons/getnotifications') || relevantPath.includes('notifications')) {
      return this.getNotificationsResponse();
    }

    // Register Vendor (POST)
    if (methodUpper === 'POST' && (relevantPath.includes('registervendor') || relevantPath.includes('register'))) {
      return this.getRegisterVendorResponse(body);
    }

    // Update Vendor (PUT/POST)
    if ((methodUpper === 'PUT' || methodUpper === 'POST') && (relevantPath.includes('updatevendor') || relevantPath.includes('vendor/update'))) {
      return this.getUpdateVendorResponse(body);
    }

    // Vendor Profile / GetVendorDetails
    if (relevantPath.includes('vendor/getprofile') ||
      relevantPath.includes('vendor/getvendordetails') ||
      (relevantPath.includes('vendor') && methodUpper === 'GET' && (relevantPath.includes('getvendordetails') || relevantPath.includes('getprofile')))) {
      console.log('✅ Matched: Vendor GetProfile/GetVendorDetails');
      return this.getVendorProfileResponse();
    }

    // Submit Bank Account (POST)
    if (methodUpper === 'POST' && (relevantPath.includes('bankapi/submitbankaccount') || relevantPath.includes('submitbankaccount'))) {
      return this.getSubmitBankAccountResponse(body);
    }

    // Update Bank Account (PUT/POST)
    if ((methodUpper === 'PUT' || methodUpper === 'POST') && (relevantPath.includes('updatebankdetails') || relevantPath.includes('bank/update'))) {
      return this.getUpdateBankAccountResponse(body);
    }

    // Bank Accounts
    if (relevantPath.includes('bankapi/getbankaccounts') || relevantPath.includes('bankaccounts')) {
      return this.getBankAccountsResponse();
    }

    // Lookups
    if (relevantPath.includes('lookups/all-lookups') || relevantPath.includes('lookups')) {
      return this.getLookupsResponse();
    }

    // Dashboard - Common/GetDashboardData
    if (relevantPath.includes('common/getdashboarddata') ||
      relevantPath.includes('getdashboarddata') ||
      (relevantPath.includes('common') && relevantPath.includes('dashboard'))) {
      return this.getDashboardDataResponse();
    }

    // Documents
    if (relevantPath.includes('documents/getdocuments')) {
      return this.getDocumentsResponse();
    }

    // Upload Documents (POST)
    if (methodUpper === 'POST' && (relevantPath.includes('uploaddocuments') || relevantPath.includes('upload'))) {
      return this.getUploadDocumentsResponse(body);
    }

    // Default response - Comprehensive fallback
    console.warn('⚠️ No specific mock data found for:', url);
    console.warn('⚠️ Relevant Path:', relevantPath);
    console.warn('⚠️ Method:', methodUpper);

    // Attempt to return default data based on path
    if (relevantPath.includes('tenders') && methodUpper === 'GET' && !relevantPath.includes('bid')) {
      console.log('⚠️ Fallback: Returning Tenders data');
      return this.getTendersResponse();
    }

    if (relevantPath.includes('bids') && methodUpper === 'GET' && !relevantPath.includes('tender')) {
      console.log('⚠️ Fallback: Returning Bids data');
      return this.getBidsResponse();
    }

    return this.getDefaultResponse(methodUpper);
  }

  // ==================== TCA demo domain ====================
  private getRelativeDate(days: number, hour = 9): string {
    const date = new Date();
    date.setHours(hour, 0, 0, 0);
    date.setDate(date.getDate() + days);
    return date.toISOString();
  }

  private getQueryValue(url: string, key: string): string {
    const match = url.match(new RegExp(`[?&]${key}=([^&#]+)`, 'i'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  private getTcaPurchaseRequisitions(): any[] {
    return [
      { prNumber: 'PR-2026-0142', prName: 'Core Network Modernization', department: 'Network Engineering', requisitionPurpose: 'Upgrade national fibre backbone capacity', description: 'Supply, installation and commissioning of core routing equipment.', value: 4850000, approvedDate: '2026-08-28', requestedDate: '2026-08-10', requester: 'Khalid Al-Sulaiti', submittedBy: 'Mariam Hassan', status: 'Linked to Tender', isTC: true, linkedTender: 'TND-2026-041' },
      { prNumber: 'PR-2026-0136', prName: 'Cybersecurity Operations Support', department: 'Information Security', requisitionPurpose: 'Managed SOC and incident response services', description: 'Three-year managed security operations and incident response service.', value: 1250000, approvedDate: '2026-08-21', requestedDate: '2026-08-02', requester: 'Noora Al-Mohannadi', submittedBy: 'Mariam Hassan', status: 'Approved', isTC: true, linkedTender: null },
      { prNumber: 'PR-2026-0129', prName: 'Office IT Equipment', department: 'Corporate Services', requisitionPurpose: 'Employee laptop and monitor refresh', description: 'Replacement of end-user computing equipment.', value: 178500, approvedDate: '2026-08-12', requestedDate: '2026-07-25', requester: 'Ahmed Saeed', submittedBy: 'Mariam Hassan', status: 'Approved', isTC: false, linkedTender: null }
    ];
  }

  private getTcaPurchaseRequisitionDetails(url: string): any {
    const number = this.getQueryValue(url, 'prNumber') || 'PR-2026-0142';
    const base = this.getTcaPurchaseRequisitions().find(pr => pr.prNumber.toLowerCase() === number.toLowerCase()) || this.getTcaPurchaseRequisitions()[0];
    return { isSuccess: true, data: {
      ...base,
      justification: 'The current platform is approaching capacity and vendor support expiry. This procurement protects service availability and future growth.',
      specifications: ['Carrier-grade routing platform', 'Five-year support and maintenance', 'Local implementation and knowledge transfer'],
      lines: [
        { prLineNumber: '10', productName: 'Core Router Platform', description: 'Redundant high-capacity core routers', quantity: 2, unit: 'EA', unitPrice: 1650000, totalAmount: 3300000 },
        { prLineNumber: '20', productName: 'Implementation Services', description: 'Design, migration, testing and commissioning', quantity: 1, unit: 'LOT', unitPrice: 950000, totalAmount: 950000 },
        { prLineNumber: '30', productName: 'Support and Training', description: 'Five-year support with technical training', quantity: 1, unit: 'LOT', unitPrice: 600000, totalAmount: 600000 }
      ],
      documents: [
        { name: 'Approved Purchase Requisition.txt', fileSize: 24576, createdAt: '2026-08-28', mimeType: 'text/plain', fileContent: 'data:text/plain;base64,UU5CTiBEZW1vIC0gQXBwcm92ZWQgUHVyY2hhc2UgUmVxdWlzaXRpb24=' },
        { name: 'Technical Scope.txt', fileSize: 18432, createdAt: '2026-08-27', mimeType: 'text/plain', fileContent: 'data:text/plain;base64,UU5CTiBEZW1vIC0gVGVjaG5pY2FsIFNjb3Bl' }
      ],
      evaluationType: 'Separate', technicalWeightage: 70, commercialWeightage: 30,
      technicalEvaluationCriteria: [{ parameter: 'Technical compliance', maximumMark: 40 }, { parameter: 'Implementation methodology', maximumMark: 30 }],
      commercialEvaluationCriteria: [{ parameter: 'Commercial offer', maximumMark: 30 }]
    }};
  }

  private getTcaTenderRecords(): any[] {
    const workflowStages = [
      { stageNumber: 1, stepName: 'Submit', responsibleRole: 'Secretary', description: 'Submit tender for committee approval' },
      { stageNumber: 2, stepName: 'Approve', responsibleRole: 'Admin', description: 'Review and approve tender' },
      { stageNumber: 3, stepName: 'Publish', responsibleRole: 'Secretary', description: 'Publish to invited vendors' }
    ];
    return [
      { tenderID: 'TND-2026-041', tenderNumber: 'TND-2026-041', tenderName: 'Core Network Modernization', description: 'Core Network Modernization', purchaseRequisition: 'PR-2026-0142', linkedPR: 'PR-2026-0142', typeofTender: 'Public Tender', tenderType: 'Public Tender', scope: 2, acceptanceDeadline: this.getRelativeDate(24), bidClosureDate: this.getRelativeDate(31), startDateOfTender: this.getRelativeDate(-8), status: 'Open', workflowStatus: 'Published', totalInvitedMembers: 6, tenderValue: 4850000, currency: 'QAR', euDepartment: 'NET', euDepartmentSpoc: 'Khalid Al-Sulaiti', managedBySpoc: 'Mariam Hassan', managedByContactDetails: 'mariam.hassan@bidaya.com / +974 4400 1100', rfpRfq: 'RFP', tenderClassification: 'Class A', company: 'Bidaya', backgroundAndSOW: 'Upgrade the national fibre backbone core with resilient routing, implementation and support.', technicalEvaluation: 'Technical compliance and delivery methodology', commercialEvaluation: 'Total evaluated price', isJoiningFeeRequired: true, joiningFee: 2500, tenderBondRequired: true, tenderBondDetails: '2% of tender value', performanceBondRequired: true, performanceBondDetails: '10% of award value', createdBy: 'Omar Al-Nuaimi', createdAt: '2026-08-29T08:30:00Z', modifiedBy: 'Aisha Al-Kuwari', modifiedAt: '2026-09-02T10:15:00Z', submittedBy: 'Omar Al-Nuaimi', submittedAt: '2026-08-30T09:00:00Z', approvedBy: 'Aisha Al-Kuwari', approvedAt: '2026-09-01T11:20:00Z', assignedToSubmit: 'secretary@demo.qa', assignedToApprove: 'committee@demo.qa', assignedToPublish: 'secretary@demo.qa', workflowStages, workflowHistory: [{ id: 1, action: 'Submit', fromStatus: 'NotSubmitted', toStatus: 'PendingApproval', comments: 'Scope and evaluation criteria completed.', actionBy: 'Omar Al-Nuaimi', actionDate: '2026-08-30T09:00:00Z' }, { id: 2, action: 'Approve', fromStatus: 'PendingApproval', toStatus: 'Approved', comments: 'Approved for publication.', actionBy: 'Aisha Al-Kuwari', actionDate: '2026-09-01T11:20:00Z' }, { id: 3, action: 'Publish', fromStatus: 'Approved', toStatus: 'Published', comments: 'Published to qualified vendors.', actionBy: 'Omar Al-Nuaimi', actionDate: '2026-09-02T10:15:00Z' }] },
      { tenderID: 'TND-2026-044', tenderNumber: 'TND-2026-044', tenderName: 'Managed Cybersecurity Operations', description: 'Managed Cybersecurity Operations', purchaseRequisition: 'PR-2026-0136', linkedPR: 'PR-2026-0136', typeofTender: 'Limited Tender', tenderType: 'Limited Tender', scope: 2, acceptanceDeadline: this.getRelativeDate(38), bidClosureDate: this.getRelativeDate(45), startDateOfTender: this.getRelativeDate(2), status: 'Pending Publication', workflowStatus: 'Approved', totalInvitedMembers: 4, tenderValue: 1250000, currency: 'QAR', euDepartment: 'SEC', company: 'Bidaya', backgroundAndSOW: 'Managed SOC monitoring and incident response services.', createdBy: 'Omar Al-Nuaimi', createdAt: '2026-09-08T08:30:00Z', modifiedBy: 'Aisha Al-Kuwari', modifiedAt: '2026-09-12T10:00:00Z', submittedBy: 'Omar Al-Nuaimi', submittedAt: '2026-09-10T09:00:00Z', approvedBy: 'Aisha Al-Kuwari', approvedAt: '2026-09-12T10:00:00Z', assignedToPublish: 'secretary@demo.qa', workflowStages, workflowHistory: [{ id: 4, action: 'Submit', fromStatus: 'NotSubmitted', toStatus: 'PendingApproval', comments: 'Submitted for review.', actionBy: 'Omar Al-Nuaimi', actionDate: '2026-09-10T09:00:00Z' }, { id: 5, action: 'Approve', fromStatus: 'PendingApproval', toStatus: 'Approved', comments: 'Approved.', actionBy: 'Aisha Al-Kuwari', actionDate: '2026-09-12T10:00:00Z' }] },
      { tenderID: 'PRA-2026-018', tenderNumber: 'PRA-2026-018', tenderName: 'Emergency Fibre Repair Services', description: 'Emergency Fibre Repair Services', purchaseRequisition: 'PR-2026-0124', linkedPR: 'PR-2026-0124', typeofTender: 'Practice', tenderType: 'Practice', scope: 0, acceptanceDeadline: this.getRelativeDate(16), bidClosureDate: this.getRelativeDate(20), startDateOfTender: this.getRelativeDate(-4), status: 'Open', workflowStatus: 'Published', totalInvitedMembers: 3, tenderValue: 720000, currency: 'QAR', euDepartment: 'OPS', company: 'Bidaya', backgroundAndSOW: 'On-call emergency fibre repair and restoration services.', createdBy: 'Omar Al-Nuaimi', modifiedBy: 'Omar Al-Nuaimi', modifiedAt: '2026-09-10T10:00:00Z', workflowStages, workflowHistory: [{ id: 6, action: 'Publish', fromStatus: 'Approved', toStatus: 'Published', comments: 'Practice published.', actionBy: 'Omar Al-Nuaimi', actionDate: '2026-09-10T10:00:00Z' }] },
      { tenderID: 'PRA-2026-021', tenderNumber: 'PRA-2026-021', tenderName: 'Data Centre Preventive Maintenance', description: 'Data Centre Preventive Maintenance', purchaseRequisition: 'PR-2026-0131', linkedPR: 'PR-2026-0131', typeofTender: 'Practice', tenderType: 'Practice', scope: 0, acceptanceDeadline: this.getRelativeDate(29), bidClosureDate: this.getRelativeDate(34), startDateOfTender: this.getRelativeDate(1), status: 'Draft', workflowStatus: 'NotSubmitted', totalInvitedMembers: 0, tenderValue: 410000, currency: 'QAR', euDepartment: 'DC', company: 'Bidaya', backgroundAndSOW: 'Quarterly preventive maintenance for critical data-centre systems.', createdBy: 'Omar Al-Nuaimi', workflowStages, workflowHistory: [] },
      { tenderID: 'FWA-PRA-2026-009', tenderNumber: 'FWA-PRA-2026-009', tenderName: 'FWA Civil Works Call-Off', description: 'FWA Civil Works Call-Off', purchaseRequisition: 'PR-2026-0117', linkedPR: 'PR-2026-0117', typeofTender: 'FWA Practice', tenderType: 'FWA Practice', scope: 1, acceptanceDeadline: this.getRelativeDate(12), bidClosureDate: this.getRelativeDate(18), startDateOfTender: this.getRelativeDate(-2), status: 'Open', workflowStatus: 'Published', totalInvitedMembers: 5, tenderValue: 980000, currency: 'QAR', euDepartment: 'PMO', company: 'Bidaya', backgroundAndSOW: 'Call-off package for ducts, chambers and reinstatement works.', createdBy: 'Omar Al-Nuaimi', modifiedBy: 'Omar Al-Nuaimi', modifiedAt: '2026-09-11T09:00:00Z', workflowStages, workflowHistory: [{ id: 7, action: 'Publish', fromStatus: 'Approved', toStatus: 'Published', comments: 'FWA practice published.', actionBy: 'Omar Al-Nuaimi', actionDate: '2026-09-11T09:00:00Z' }] },
      { tenderID: 'FWA-PRA-2026-010', tenderNumber: 'FWA-PRA-2026-010', tenderName: 'FWA Network Equipment Call-Off', description: 'FWA Network Equipment Call-Off', purchaseRequisition: 'PR-2026-0120', linkedPR: 'PR-2026-0120', typeofTender: 'FWA Practice', tenderType: 'FWA Practice', scope: 1, acceptanceDeadline: this.getRelativeDate(33), bidClosureDate: this.getRelativeDate(40), startDateOfTender: this.getRelativeDate(3), status: 'Pending Approval', workflowStatus: 'PendingApproval', totalInvitedMembers: 4, tenderValue: 1450000, currency: 'QAR', euDepartment: 'NET', company: 'Bidaya', backgroundAndSOW: 'Call-off package for access-network equipment.', createdBy: 'Omar Al-Nuaimi', modifiedBy: 'Omar Al-Nuaimi', modifiedAt: '2026-09-14T09:30:00Z', workflowStages, workflowHistory: [{ id: 8, action: 'Submit', fromStatus: 'NotSubmitted', toStatus: 'PendingApproval', comments: 'Ready for approval.', actionBy: 'Omar Al-Nuaimi', actionDate: '2026-09-14T09:30:00Z' }] }
    ];
  }

  private getTcaTenderList(path: string): any {
    const scope = Number((path.match(/[?&]scope=(\d+)/)?.[1] ?? 2));
    return { isSuccess: true, data: this.getTcaTenderRecords().filter(t => t.scope === scope) };
  }

  private getTcaTenderDetails(url: string): any {
    const requested = this.getQueryValue(url, 'tenderNumber');
    const tender = this.getTcaTenderRecords().find(t => t.tenderNumber.toLowerCase() === requested.toLowerCase()) || this.getTcaTenderRecords()[0];
    return { isSuccess: true, data: { ...tender, version: '1', rfpRfq: tender.rfpRfq || 'RFP', tenderClassification: tender.tenderClassification || 'Class A', tenderValueInQAR: tender.tenderValue, selectedVendors: this.getTcaVendorRows().slice(0, tender.totalInvitedMembers || 3), evaluationType: 'Separate', technicalWeightage: 70, commercialWeightage: 30, technicalEvaluationCriteria: [{ serialNo: 1, parameter: 'Technical compliance', maximumMark: 40 }, { serialNo: 2, parameter: 'Implementation methodology', maximumMark: 30 }], commercialEvaluationCriteria: [{ serialNo: 1, parameter: 'Evaluated price', maximumMark: 30 }] } };
  }

  private getTcaVendorRows(): any[] {
    return [
      { vendorID: 'VND-10021', vendorId: 'VND-10021', fullName: 'Gulf Digital Solutions W.L.L.', vendorName: 'Gulf Digital Solutions W.L.L.', status: 'Approved', vendorType: 'Supplier', localOrForeign: 'Local' },
      { vendorID: 'VND-10034', vendorId: 'VND-10034', fullName: 'Doha Network Systems', vendorName: 'Doha Network Systems', status: 'Approved', vendorType: 'Contractor', localOrForeign: 'Local' },
      { vendorID: 'VND-10048', vendorId: 'VND-10048', fullName: 'Nexa Infrastructure Ltd.', vendorName: 'Nexa Infrastructure Ltd.', status: 'Approved', vendorType: 'Supplier', localOrForeign: 'Foreign' }
    ];
  }

  private getTcaBids(): any {
    return { isSuccess: true, data: [
      { bidId: 'BID-2026-041-01', tenderId: 'TND-2026-041', vendorId: 'VND-10021', vendorName: 'Gulf Digital Solutions W.L.L.', submissionDeadline: this.getRelativeDate(31), status: 'Submitted', tcComitteeStatus: 'Under Evaluation', evaluationType: 'Separate', totalScore: 86, votingStatus: 'Pending', isBidLocked: false, isBidDecisionFinal: false },
      { bidId: 'BID-2026-041-02', tenderId: 'TND-2026-041', vendorId: 'VND-10034', vendorName: 'Doha Network Systems', submissionDeadline: this.getRelativeDate(31), status: 'Submitted', tcComitteeStatus: 'Recommended', evaluationType: 'Separate', totalScore: 91, votingStatus: 'Completed', decision: 'Award', isBidLocked: false, isBidDecisionFinal: true },
      { bidId: 'PRA-2026-018-B01', tenderId: 'PRA-2026-018', vendorId: 'VND-10048', vendorName: 'Nexa Infrastructure Ltd.', submissionDeadline: this.getRelativeDate(20), status: 'Submitted', tcComitteeStatus: 'Pending Review', evaluationType: 'Joint', totalScore: 79, votingStatus: 'Pending', isBidLocked: false, isBidDecisionFinal: false }
    ]};
  }

  private getTcaBidDetails(path: string): any {
    const bidId = decodeURIComponent(path.split('getbiddetailsbybidid/')[1]?.split('?')[0] || 'BID-2026-041-01').toUpperCase();
    const row = this.getTcaBids().data.find((b: any) => b.bidId.toUpperCase() === bidId) || this.getTcaBids().data[0];
    return { isSuccess: true, data: { ...row, contactName: 'Fatima Al-Hamad', contactPhone: '+974 5550 2200', contactEmail: 'tenders@gulfdigital.qa', bidSubmissionDate: '2026-09-12T13:30:00Z', bidValue: 4325000, deliveryPeriodInDays: 150, paymentTerms: '30 days from certified invoice', warrantyPeriodInYears: 5, joiningFee: 2500, paymentReferenceNumber: 'PAY-JF-88214', accountNumber: 'QA58QNBA000000123456789012345', bankName: 'Qatar National Bank', decision: row.decision || '', tcComitteeStatus: row.tcComitteeStatus } };
  }

  private getTcaTenderBids(): any {
    return { isSuccess: true, data: this.getTcaBids().data.map((b: any) => ({ bidID: b.bidId, vendorID: b.vendorId, vendorName: b.vendorName, bidSubmissionDate: '2026-09-12T13:30:00Z', bidValue: b.bidId.endsWith('02') ? 4180000 : 4325000, status: b.status, tcComitteeStatus: b.tcComitteeStatus })) };
  }

  private getTcaMeetingUsers(): any[] {
    return [
      { id: 'USR-CHAIR', fullName: 'Eng. Mohammed Al-Marri', email: 'chairman@demo.qa', role: 'Chairman' },
      { id: 'USR-SEC', fullName: 'Omar Al-Nuaimi', email: 'secretary@demo.qa', role: 'Secretary' },
      { id: 'USR-M01', fullName: 'Aisha Al-Kuwari', email: 'committee@demo.qa', role: 'Member' },
      { id: 'USR-M02', fullName: 'Sara Ahmed', email: 'member@demo.qa', role: 'Member' },
      { id: 'USR-OBS', fullName: 'Yousef Ibrahim', email: 'observer@demo.qa', role: 'Observer' }
    ];
  }

  private getTcaAgendaItems(): any[] {
    return [
      { id: 101, order: 1, subject: 'Core Network Modernization – Technical Evaluation', agendaTitleEN: 'Core Network Modernization – Technical Evaluation', agendaTitleAR: 'التقييم الفني لتحديث الشبكة الأساسية', descriptionEnglish: 'Review the technical evaluation and approve opening of commercial proposals.', descriptionArabic: 'مراجعة التقييم الفني والموافقة على فتح العروض التجارية.', prIdentifiers: ['PR-2026-0142'], currentStage: 'Technical Evaluation Review', requiredFromTC: 'Approve technical evaluation', department: 'Network Engineering', status: 2, statusName: 'In Progress', createdBy: 'Omar Al-Nuaimi', createdAt: '2026-09-08T09:00:00Z', meetingId: 'TCM-2026-019', tcNotes: 'All vendors passed mandatory compliance checks.' },
      { id: 102, order: 2, subject: 'Cybersecurity Operations Tender Publication', agendaTitleEN: 'Cybersecurity Operations Tender Publication', agendaTitleAR: 'نشر مناقصة عمليات الأمن السيبراني', descriptionEnglish: 'Review tender documents and approve publication.', descriptionArabic: 'مراجعة وثائق المناقصة والموافقة على النشر.', prIdentifiers: ['PR-2026-0136'], currentStage: 'Tender Approval', requiredFromTC: 'Approve publication', department: 'Information Security', status: 1, statusName: 'Open', createdBy: 'Omar Al-Nuaimi', createdAt: '2026-09-12T11:00:00Z', meetingId: 'TCM-2026-018', tcNotes: 'Proposed shortlist contains four qualified vendors.' },
      { id: 103, order: 1, subject: 'Award Recommendation – Data Centre UPS Replacement', agendaTitleEN: 'Award Recommendation – Data Centre UPS Replacement', agendaTitleAR: 'توصية الترسية لاستبدال وحدات الطاقة', descriptionEnglish: 'Consider the final evaluation and award recommendation.', descriptionArabic: 'النظر في التقييم النهائي وتوصية الترسية.', prIdentifiers: ['PR-2026-0118'], currentStage: 'Award Decision', requiredFromTC: 'Approve award', department: 'Data Centre', status: 3, statusName: 'Completed', createdBy: 'Omar Al-Nuaimi', createdAt: '2026-09-01T08:30:00Z', meetingId: 'TCM-2026-017', tcNotes: 'Recommended bidder achieved the highest combined score.', tcDecisionEN: 'Award to Doha Network Systems for QAR 2,180,000.', tcDecisionAR: 'الترسية على شركة أنظمة شبكة الدوحة بمبلغ 2,180,000 ريال قطري.' },
      { id: 104, order: 2, subject: 'FWA Civil Works Call-Off Approval', agendaTitleEN: 'FWA Civil Works Call-Off Approval', agendaTitleAR: 'اعتماد طلب أعمال مدنية', descriptionEnglish: 'Approve the FWA call-off package and invited vendors.', descriptionArabic: 'اعتماد حزمة الطلب والموردين المدعوين.', prIdentifiers: ['PR-2026-0117'], currentStage: 'Completed', requiredFromTC: 'Approve call-off', department: 'PMO', status: 3, statusName: 'Completed', createdBy: 'Omar Al-Nuaimi', createdAt: '2026-09-01T08:45:00Z', meetingId: 'TCM-2026-017', tcNotes: 'Budget and FWA ceiling verified.', tcDecisionEN: 'Approved unanimously for issue to the five FWA vendors.', tcDecisionAR: 'تمت الموافقة بالإجماع لإصدار الطلب إلى موردي الاتفاقية الخمسة.' }
    ];
  }

  private getTcaMeetingRows(): any[] {
    const members = this.getTcaMeetingUsers().map(u => ({ userId: u.id, userName: u.fullName, userEmail: u.email, role: u.role, statusName: 'Accepted' }));
    return [
      { id: 19, meetingId: 'TCM-2026-019', meetingTitle: 'Tender Committee Meeting 19/2026', dateTime: this.getRelativeDate(0, 10), agendaCount: 1, status: 3, statusName: 'OnGoing', momStageName: 'Draft', location: 'Bidaya Board Room', chairmanName: 'Eng. Mohammed Al-Marri', createdBy: 'Omar Al-Nuaimi', createdAt: '2026-09-12T08:00:00Z', agendaNotes: 'Current live committee session.', members, agendaItems: this.getTcaAgendaItems().filter(a => a.meetingId === 'TCM-2026-019'), memberAttendances: this.getTcaAttendance() },
      { id: 18, meetingId: 'TCM-2026-018', meetingTitle: 'Tender Committee Meeting 18/2026', dateTime: this.getRelativeDate(2, 10), agendaCount: 1, status: 2, statusName: 'WaitingForAttendance', momStageName: 'Draft', location: 'Bidaya Board Room', chairmanName: 'Eng. Mohammed Al-Marri', createdBy: 'Omar Al-Nuaimi', createdAt: '2026-09-11T08:00:00Z', agendaNotes: 'Members may record attendance before the meeting begins.', members, agendaItems: this.getTcaAgendaItems().filter(a => a.meetingId === 'TCM-2026-018'), memberAttendances: this.getTcaAttendance().slice(0, 3) },
      { id: 17, meetingId: 'TCM-2026-017', meetingTitle: 'Tender Committee Meeting 17/2026', dateTime: '2026-09-10T09:30:00Z', agendaCount: 2, status: 5, statusName: 'Completed', momStageName: 'Completed', location: 'Bidaya Board Room', chairmanName: 'Eng. Mohammed Al-Marri', createdBy: 'Omar Al-Nuaimi', createdAt: '2026-09-04T08:00:00Z', agendaNotes: 'Final award and FWA call-off decisions.', members, agendaItems: this.getTcaAgendaItems().filter(a => a.meetingId === 'TCM-2026-017'), memberAttendances: this.getTcaAttendance() }
    ];
  }

  private getTcaMeetings(path: string): any {
    const completedOnly = /[?&]status=5/.test(path);
    const rows = this.getTcaMeetingRows().filter(m => !completedOnly || m.status === 5);
    return { isSuccess: true, data: rows };
  }

  private getTcaMeetingDetails(path: string): any {
    const key = decodeURIComponent(path.split('/')[1]?.split('?')[0] || '').toLowerCase();
    const meeting = this.getTcaMeetingRows().find(m => String(m.id) === key || m.meetingId.toLowerCase() === key) || this.getTcaMeetingRows()[0];
    return { isSuccess: true, data: meeting };
  }

  private getTcaAttendance(): any[] {
    return [
      { userId: 'USR-CHAIR', userName: 'Eng. Mohammed Al-Marri', userNameAr: 'م. محمد المري', role: 'Chairman', attended: true },
      { userId: 'USR-SEC', userName: 'Omar Al-Nuaimi', userNameAr: 'عمر النعيمي', role: 'Secretary', attended: true },
      { userId: 'USR-M01', userName: 'Aisha Al-Kuwari', userNameAr: 'عائشة الكواري', role: 'Member', attended: true },
      { userId: 'USR-M02', userName: 'Sara Ahmed', userNameAr: 'سارة أحمد', role: 'Member', attended: true },
      { userId: 'USR-OBS', userName: 'Yousef Ibrahim', userNameAr: 'يوسف إبراهيم', role: 'Observer', attended: true }
    ];
  }

  private getTcaMeetingDecisions(path: string): any {
    const meetingKey = path.split('/')[1]?.toLowerCase() || '';
    const isCompleted = meetingKey === '17' || meetingKey === 'tcm-2026-017';
    const meetingId = isCompleted ? 'TCM-2026-017' : (meetingKey.includes('018') || meetingKey === '18' ? 'TCM-2026-018' : 'TCM-2026-019');
    const agendaItems = this.getTcaAgendaItems().filter(a => a.meetingId === meetingId);
    return { isSuccess: true, data: {
      agendaItems: agendaItems.map(a => ({ agendaId: a.id, order: a.order, subject: a.subject, agendaTitleEN: a.agendaTitleEN, agendaTitleAR: a.agendaTitleAR })),
      decisions: isCompleted ? agendaItems.map(a => ({ agendaId: a.id, tcDecisionEN: a.tcDecisionEN, tcDecisionAR: a.tcDecisionAR })) : [],
      responses: isCompleted ? agendaItems.flatMap(a => [
        { agendaId: a.id, userId: 'USR-CHAIR', userName: 'Eng. Mohammed Al-Marri', response: 'Accepted' },
        { agendaId: a.id, userId: 'USR-M01', userName: 'Aisha Al-Kuwari', response: 'Accepted' },
        { agendaId: a.id, userId: 'USR-M02', userName: 'Sara Ahmed', response: 'Accepted' }
      ]) : []
    }};
  }

  private getTcaAgendaItemDetails(path: string): any {
    const id = Number(path.split('/').pop());
    return { isSuccess: true, data: this.getTcaAgendaItems().find(a => a.id === id) || this.getTcaAgendaItems()[0] };
  }

  private getTcaUnsealedBids(): any { return { isSuccess: true, data: this.getTcaBids().data.map((b: any) => ({ ...b, isUnsealed: true })) }; }
  private getTcaEvaluationReviews(): any { return { isSuccess: true, data: [{ evaluationId: 'EVA-2026-031', evaluatorName: 'Sara Ahmed', technicalScore: 61, commercialScore: 25, totalScore: 86, status: 'Reviewed' }] }; }
  private getTcaAwardBids(): any { return { isSuccess: true, data: this.getTcaBids().data.filter((b: any) => b.decision === 'Award') }; }

  private getTcaLookup(path: string): any {
    const type = path.split('/')[1]?.split('?')[0] || '';
    if (type === 'all-lookups') {
      return this.getLookupsResponse();
    }
    const data: Record<string, any[]> = {
      currencies: [{ id: 1, currencyId: 'QAR', description: 'Qatari Riyal', isDefaultValue: true }, { id: 2, currencyId: 'USD', description: 'US Dollar', isDefaultValue: false }, { id: 3, currencyId: 'EUR', description: 'Euro', isDefaultValue: false }],
      countries: [{ id: 1, countryRegionId: 'QA', countryName: 'Qatar' }, { id: 2, countryRegionId: 'AE', countryName: 'United Arab Emirates' }, { id: 3, countryRegionId: 'SA', countryName: 'Saudi Arabia' }],
      'methods-of-payment': [{ id: 1, methodOfPaymentId: 'BANK', description: 'Bank Transfer', isDefaultValue: true }, { id: 2, methodOfPaymentId: 'LC', description: 'Letter of Credit', isDefaultValue: false }],
      'terms-of-payment': [{ id: 1, termsOfPaymentId: 'NET30', description: '30 days from invoice', isDefaultValue: true }, { id: 2, termsOfPaymentId: 'MILESTONE', description: 'Milestone based', isDefaultValue: false }],
      departments: [{ id: 1, departmentId: 'NET', departmentName: 'Network Engineering', isDefaultValue: false }, { id: 2, departmentId: 'SEC', departmentName: 'Information Security', isDefaultValue: false }, { id: 3, departmentId: 'PMO', departmentName: 'Project Management Office', isDefaultValue: false }],
      banks: [{ id: 1, bankId: 'QNB', description: 'Qatar National Bank', isDefaultValue: true }, { id: 2, bankId: 'CBQ', description: 'Commercial Bank of Qatar', isDefaultValue: false }],
      'documents-master': [{ id: 1, documentName: 'Commercial Registration', section: 'Company', processName: 'Vendor Registration', parmDocumentFor: 'Vendor', documentType: 'PDF', mandatory: true, fieldHint: 'Valid CR copy', expirationDate: true, expirationDateMandatory: true, allowMultipleDocs: false, checkBoxRequired: false, additionalDetailsRequired: false, position: 1 }, { id: 2, documentName: 'Technical Proposal', section: 'Technical', processName: 'Bidding', parmDocumentFor: 'Bid', documentType: 'PDF', mandatory: true, fieldHint: 'Signed technical proposal', expirationDate: false, expirationDateMandatory: false, allowMultipleDocs: true, checkBoxRequired: true, additionalDetailsRequired: false, position: 2 }],
      categories: [{ id: 1, categoryId: 'ICT', description: 'Information and Communication Technology' }, { id: 2, categoryId: 'CIVIL', description: 'Civil Works' }],
      subcategories: [{ id: 11, subCategoryId: 'ICT-NET', categoryId: 'ICT', description: 'Network Infrastructure' }, { id: 12, subCategoryId: 'ICT-SEC', categoryId: 'ICT', description: 'Cybersecurity' }, { id: 21, subCategoryId: 'CIVIL-OUT', categoryId: 'CIVIL', description: 'Outside Plant Works' }],
      subsubcategories: [{ id: 111, subSubCategoryId: 'ICT-NET-ROUTER', subCategoryId: 'ICT-NET', description: 'Routers and Switching' }, { id: 121, subSubCategoryId: 'ICT-SEC-SOC', subCategoryId: 'ICT-SEC', description: 'Security Operations' }, { id: 211, subSubCategoryId: 'CIVIL-OUT-DUCT', subCategoryId: 'CIVIL-OUT', description: 'Ducts and Chambers' }]
    };
    return { isSuccess: true, data: data[type] || [] };
  }

  private getTcaEvaluationDetails(): any {
    return {
      isSuccess: true,
      evaluationId: 'EVA-2026-031', tenderId: 'TND-2026-041', prNumber: 'PR-2026-0142', tenderTitle: 'Core Network Modernization',
      evaluationType: 'Separate', evaluatorId: 'TEAM-TECH', comments: 'Technical and commercial evaluations consolidated for committee review.', recommendation: 'Proceed with the highest-ranked compliant bidder.', status: 'Pending Review', reviewComments: '', score: 86,
      assignmentInfo: { assignedToEn: 'Technical Evaluation Team', assignedToAr: 'فريق التقييم الفني', assignedDate: '2026-09-03T09:00:00Z', submittedDate: '2026-09-12T15:00:00Z', assignedEvaluators: [{ userId: 'USR-M02', fullName: 'Sara Ahmed', nameAr: 'سارة أحمد', email: 'member@demo.qa' }, { userId: 'USR-TECH', fullName: 'Hamad Al-Sayed', nameAr: 'حمد السيد', email: 'evaluator@demo.qa' }] },
      timelineEvents: [{ title: 'Evaluation assigned', date: '2026-09-03T09:00:00Z', type: 'assigned' }, { title: 'Evaluation started', date: '2026-09-04T08:30:00Z', type: 'started' }, { title: 'Evaluation submitted', date: '2026-09-12T15:00:00Z', type: 'submitted' }],
      allBids: [{ bidID: 'BID-2026-041-01', vendorName: 'Gulf Digital Solutions W.L.L.', generalRemarks: 'Fully compliant; strong implementation plan.', status: 'Evaluated', actionTaken: 'Recommended', isCurrent: true }, { bidID: 'BID-2026-041-02', vendorName: 'Doha Network Systems', generalRemarks: 'Compliant and commercially competitive.', status: 'Evaluated', actionTaken: 'Ranked First', isCurrent: false }]
    };
  }

  private getTcaEvaluationScoring(): any {
    return { isSuccess: true, data: {
      evaluationId: 'EVA-2026-031', tenderId: 'TND-2026-041', prNumber: 'PR-2026-0142', evaluationType: 'Separate',
      criteria: [{ id: 1, parameter: 'Technical compliance', maximumMark: 40, marksScored: 36, comments: 'All mandatory specifications met.' }, { id: 2, parameter: 'Implementation methodology', maximumMark: 30, marksScored: 25, comments: 'Detailed migration and rollback plan.' }, { id: 3, parameter: 'Commercial offer', maximumMark: 30, marksScored: 25, comments: 'Competitive evaluated price.' }],
      totalScore: 86, recommendation: 'Recommended for committee review'
    }};
  }

  private getTcaMinutesDetails(): any {
    const decisions = this.getTcaAgendaItems().filter(a => a.meetingId === 'TCM-2026-017').map(a => ({ agendaId: a.id, order: a.order, subject: a.subject, agendaTitleEN: a.agendaTitleEN, agendaTitleAR: a.agendaTitleAR, tcDecisionEN: a.tcDecisionEN, tcDecisionAR: a.tcDecisionAR, acceptedVotes: 3, rejectedVotes: 0, deferredVotes: 0 }));
    return { isSuccess: true, data: {
      basicInfo: { meetingId: 'TCM-2026-017', meetingTitle: 'Tender Committee Meeting 17/2026', dateTime: '2026-09-10T09:30:00Z', createdAt: '2026-09-10T12:00:00Z', location: 'Bidaya Board Room' },
      attendance: this.getTcaAttendance().map((a, index) => ({ ...a, userId: index + 1, momApprovalStatusName: 'Approved' })),
      agendaDecisions: decisions, momStage: 3, momStageName: 'Completed'
    }};
  }

  private getTcaFaqDetails(path: string): any {
    const id = Number(path.split('/').pop()) || 1;
    return { isSuccess: true, data: { id, faqId: `FAQ-2026-${String(id).padStart(3, '0')}`, linkedTenderId: 'TND-2026-041', linkedTenderTitle: 'Core Network Modernization', question: 'Can the bid closing date be extended?', answer: 'Extension requests are reviewed by the Tender Committee and communicated to every invited vendor.', questionAr: 'هل يمكن تمديد موعد إغلاق العطاء؟', answerAr: 'تتم مراجعة طلبات التمديد من قبل لجنة المناقصات وإبلاغ جميع الموردين المدعوين.', status: 'Published', createdBy: 'Omar Al-Nuaimi', createdDate: '2026-09-05T09:00:00Z', modifiedBy: 'Aisha Al-Kuwari', modifiedDate: '2026-09-06T11:20:00Z' } };
  }

  private getTcaVendorDetails(url: string): any {
    const section = this.getQueryValue(url, 'section').toLowerCase();
    if (section === 'contacts') {
      return { isSuccess: true, data: [{ id: 1, name: 'Fatima Al-Hamad', title: 'Tender Manager', email: 'tenders@gulfdigital.qa', mobileNumber: '+974 5550 2200', contactType: 'Primary', status: 'Active' }, { id: 2, name: 'Rashid Karim', title: 'Finance Manager', email: 'finance@gulfdigital.qa', mobileNumber: '+974 5550 2201', contactType: 'Secondary', status: 'Active' }] };
    }
    if (section === 'workflow') {
      return { isSuccess: true, data: { vendorID: 'VND-10021', status: 'Submitted', assignedToEmail: 'committee@demo.qa', assignedToApproveDisplay: 'Aisha Al-Kuwari', canCurrentUserApprove: true, workflowStages: [{ stageNumber: 1, stepName: 'Application Submitted', responsibleRole: 'Vendor', status: 'Completed' }, { stageNumber: 2, stepName: 'Compliance Review', responsibleRole: 'Admin', status: 'Completed' }, { stageNumber: 3, stepName: 'Committee Approval', responsibleRole: 'Admin', status: 'Active' }], workflowHistory: [{ action: 'Submit', fromStatus: 'Draft', toStatus: 'Submitted', comments: 'Vendor application submitted with all documents.', actionBy: 'Fatima Al-Hamad', actionDate: '2026-09-08T10:00:00Z' }, { action: 'Compliance Review', fromStatus: 'Submitted', toStatus: 'Under Review', comments: 'Commercial registration and banking details verified.', actionBy: 'Omar Al-Nuaimi', actionDate: '2026-09-11T14:30:00Z' }], childTasks: [{ id: 1, title: 'Validate ISO certificate', assignedToEmail: 'quality@bidaya.com', status: 'Completed' }] } };
    }
    return { isSuccess: true, data: { vendorID: 'VND-10021', fullName: 'Gulf Digital Solutions W.L.L.', vendorType: 'Organization', status: 'Submitted', localOrForeign: 'Local', commercialRegistrationNumber: 'CR-884210', establishmentCountry: 'QA', establishmentYear: 2014, noOfEmployees: 86, natureOfBusiness: 'ICT infrastructure and managed services', website: 'https://example.invalid', financeEmail: 'finance@gulfdigital.qa', selectedSubSubCategories: ['ICT-NET-ROUTER', 'ICT-SEC-SOC'], bankAccounts: this.getTcaVendorBankAccounts().data } };
  }

  private getTcaVendorBankAccounts(): any {
    return { isSuccess: true, data: [{ id: 'BANK-101', bankName: 'Qatar National Bank', accountNumber: '00123456789', bankIBAN: 'QA58QNBA000000123456789012345', swift: 'QNBAQAQA', beneficiaryName: 'Gulf Digital Solutions W.L.L.', currencyCode: 'QAR', status: 'Submitted', canCurrentUserApprove: true, createdAt: '2026-09-08T10:15:00Z' }] };
  }

  // ==================== Authentication ====================
  private getAuthenticateResponse(): any {
    return {
      isSuccess: true,
      data: {
        token: this.demoToken,
        refreshToken: 'tca-demo-refresh-token',
        expiresIn: 3600,
        user: {
          Id: 'VENDOR001',
          FullName: 'Mock Vendor Company',
          EmailAddress: 'vendor@example.com',
          MobilePhone: '+974 1234 5678',
          Status: 'Active'
        }
      },
      message: 'Authentication successful'
    };
  }

  private getVerifyOTPResponse(): any {
    return {
      isSuccess: true,
      data: {
        verified: true,
        token: this.demoToken,
        refreshToken: 'tca-demo-refresh-token'
      },
      message: 'OTP verified successfully'
    };
  }

  private getGenerateOTPResponse(): any {
    return {
      isSuccess: true,
      data: {
        otpSent: true,
        expiryTime: new Date(Date.now() + 5 * 60 * 1000).toISOString()
      },
      message: 'OTP sent successfully'
    };
  }

  // ==================== Tenders ====================
  private getTendersResponse(): any {
    return {
      isSuccess: true,
      isSystemError: false,
      code: 200,
      data: [
        {
          tenderID: 'TND001',
          referenceNo: 'REF-2024-001',
          startDate: '2024-01-15T00:00:00',
          closureDate: '2024-02-15T00:00:00',
          extendedDate: '2024-02-20T00:00:00',
          bidClosureDate: '2024-02-28T00:00:00',
          status: 'Open',
          publishDate: '2024-01-10T00:00:00',
          typeofTender: 'Public',
          bankName: 'Qatar National Bank',
          description: 'Construction of Office Building in Doha',
          entity: 'Qatar Foundation',
          Manageby: 'Qatar Foundation',
          TenderType: 'Public',
          action: 'Accepted',
          isNew: false,
          joiningFee: 5000,
          tenderClassification: 'A',
          isTenderBondRequired: true,
          isPerformanceBondRequired: true,
          isJoiningFeeRequired: true,
          vendorTenderParticipation: {
            tenderID: 'TND001',
            vendorID: 'VENDOR001',
            firstTimeView: false,
            joiningFee: 5000,
            isFeepaid: true
          }
        },
        {
          tenderID: 'TND002',
          referenceNo: 'REF-2024-002',
          startDate: '2024-02-01T00:00:00',
          closureDate: '2024-03-01T00:00:00',
          extendedDate: '2024-03-05T00:00:00',
          bidClosureDate: '2024-03-10T00:00:00',
          status: 'Open',
          publishDate: '2024-01-25T00:00:00',
          typeofTender: 'Public',
          bankName: 'Commercial Bank',
          description: 'IT Infrastructure Upgrade Project',
          entity: 'Ministry of Finance',
          Manageby: 'Ministry of Finance',
          TenderType: 'Public',
          action: 'Accepted',
          isNew: true,
          joiningFee: 3000,
          tenderClassification: 'B',
          isTenderBondRequired: false,
          isPerformanceBondRequired: true,
          isJoiningFeeRequired: true,
          vendorTenderParticipation: {
            tenderID: 'TND002',
            vendorID: 'VENDOR001',
            firstTimeView: true,
            joiningFee: 3000,
            isFeepaid: false
          }
        },
        {
          tenderID: 'TND003',
          referenceNo: 'REF-2024-003',
          startDate: '2024-01-20T00:00:00',
          closureDate: '2024-02-20T00:00:00',
          extendedDate: '2024-02-25T00:00:00',
          bidClosureDate: '2024-02-28T00:00:00',
          status: 'Open',
          publishDate: '2024-01-15T00:00:00',
          typeofTender: 'Private',
          bankName: 'Doha Bank',
          description: 'Annual Maintenance Services',
          entity: 'Qatar Airways',
          Manageby: 'Qatar Airways',
          TenderType: 'Private',
          action: 'Accepted',
          isNew: false,
          joiningFee: 2000,
          tenderClassification: 'C',
          isTenderBondRequired: false,
          isPerformanceBondRequired: false,
          isJoiningFeeRequired: true,
          vendorTenderParticipation: {
            tenderID: 'TND003',
            vendorID: 'VENDOR001',
            firstTimeView: false,
            joiningFee: 2000,
            isFeepaid: true
          }
        },
        {
          tenderID: 'TND004',
          referenceNo: 'REF-2024-004',
          startDate: '2024-02-10T00:00:00',
          closureDate: '2024-03-10T00:00:00',
          extendedDate: '2024-03-15T00:00:00',
          bidClosureDate: '2024-03-20T00:00:00',
          status: 'Open',
          publishDate: '2024-02-05T00:00:00',
          typeofTender: 'Public',
          bankName: 'Qatar Islamic Bank',
          description: 'Supply of Office Furniture and Equipment',
          entity: 'Ministry of Education',
          Manageby: 'Ministry of Education',
          TenderType: 'Public',
          action: 'Accepted',
          isNew: false,
          joiningFee: 4000,
          tenderClassification: 'B',
          isTenderBondRequired: true,
          isPerformanceBondRequired: true,
          isJoiningFeeRequired: true,
          vendorTenderParticipation: {
            tenderID: 'TND004',
            vendorID: 'VENDOR001',
            firstTimeView: false,
            joiningFee: 4000,
            isFeepaid: true
          }
        },
        {
          tenderID: 'TND005',
          referenceNo: 'REF-2024-005',
          startDate: '2024-02-15T00:00:00',
          closureDate: '2024-03-15T00:00:00',
          extendedDate: '2024-03-20T00:00:00',
          bidClosureDate: '2024-03-25T00:00:00',
          status: 'Open',
          publishDate: '2024-02-10T00:00:00',
          typeofTender: 'Private',
          bankName: 'Al Rayan Bank',
          description: 'Security Systems Installation',
          entity: 'Hamad International Airport',
          Manageby: 'Hamad International Airport',
          TenderType: 'Private',
          action: 'RejectedParticipation',
          isNew: true,
          joiningFee: 6000,
          tenderClassification: 'A',
          isTenderBondRequired: true,
          isPerformanceBondRequired: true,
          isJoiningFeeRequired: true,
          vendorTenderParticipation: {
            tenderID: 'TND005',
            vendorID: 'VENDOR001',
            firstTimeView: true,
            joiningFee: 6000,
            isFeepaid: false
          }
        }
      ],
      totalCount: 5
    };
  }

  // ==================== Tender Details ====================
  private getTenderDetailsResponse(tenderId: string): any {
    // Get tender from list or create a default one
    const tenders = this.getTendersResponse().data;
    let tender = tenders.find((t: any) => t.tenderID === tenderId);

    // If tender not found, create a default one
    if (!tender) {
      tender = {
        tenderID: tenderId,
        referenceNo: 'REF-2024-001',
        startDate: '2024-01-15T00:00:00',
        closureDate: '2024-02-15T00:00:00',
        extendedDate: '2024-02-20T00:00:00',
        bidClosureDate: '2024-02-28T00:00:00',
        status: 'Open',
        publishDate: '2024-01-10T00:00:00',
        typeofTender: 'Public',
        bankName: 'Qatar National Bank',
        description: 'Construction of Office Building in Doha',
        entity: 'Qatar Foundation',
        managedBy: 'Qatar Foundation',
        joiningFee: 5000,
        tenderClassification: 'A',
        isTenderBondRequired: true,
        isPerformanceBondRequired: true,
        isJoiningFeeRequired: true,
        action: null,
        bidID: null
      };
    }

    // Ensure managedBy is set (some mock data uses Manageby)
    if (!tender.managedBy && tender.Manageby) {
      tender.managedBy = tender.Manageby;
    }

    // Create a copy to avoid mutating the original tender from the list
    tender = { ...tender };

    // Add documents (attachments) grouped by attachmentFor
    tender.documents = [
      {
        name: 'Tender Document - Technical Specifications.pdf',
        description: 'Complete technical specifications and requirements',
        date: new Date('2024-01-10T00:00:00'),
        url: '#',
        attachmentFor: 'Tender Documents',
        fileContent: 'base64encodedcontent',
        additionalDetails: 'Technical specifications document',
        mimeType: 'application/pdf'
      },
      {
        name: 'Tender Document - Terms and Conditions.pdf',
        description: 'Terms and conditions for participation',
        date: new Date('2024-01-10T00:00:00'),
        url: '#',
        attachmentFor: 'Tender Documents',
        fileContent: 'base64encodedcontent',
        additionalDetails: 'Terms and conditions document',
        mimeType: 'application/pdf'
      },
      {
        name: 'Technical Drawings - Floor Plans.pdf',
        description: 'Detailed architectural drawings and floor plans',
        date: new Date('2024-01-12T00:00:00'),
        url: '#',
        attachmentFor: 'Technical Specifications',
        fileContent: 'base64encodedcontent',
        additionalDetails: 'Architectural drawings',
        mimeType: 'application/pdf'
      },
      {
        name: 'Technical Drawings - Electrical Layouts.pdf',
        description: 'Electrical system layouts and specifications',
        date: new Date('2024-01-12T00:00:00'),
        url: '#',
        attachmentFor: 'Technical Specifications',
        fileContent: 'base64encodedcontent',
        additionalDetails: 'Electrical layouts',
        mimeType: 'application/pdf'
      },
      {
        name: 'Bid Submission Form.docx',
        description: 'Template for bid submission',
        date: new Date('2024-01-10T00:00:00'),
        url: '#',
        attachmentFor: 'Bid Forms',
        fileContent: 'base64encodedcontent',
        additionalDetails: 'Bid submission form template',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      },
      {
        name: 'Price Schedule Template.xlsx',
        description: 'Excel template for price schedule',
        date: new Date('2024-01-10T00:00:00'),
        url: '#',
        attachmentFor: 'Bid Forms',
        fileContent: 'base64encodedcontent',
        additionalDetails: 'Price schedule template',
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    ];

    // Add evaluation form criteria
    tender.evaluationForm = [
      {
        tenderID: tenderId,
        vendorID: 'VENDOR001',
        bidID: null,
        serialNo: 1,
        parameter: 'Technical Competence and Experience',
        maximumMark: 30,
        marksScored: null
      },
      {
        tenderID: tenderId,
        vendorID: 'VENDOR001',
        bidID: null,
        serialNo: 2,
        parameter: 'Project Management and Methodology',
        maximumMark: 25,
        marksScored: null
      },
      {
        tenderID: tenderId,
        vendorID: 'VENDOR001',
        bidID: null,
        serialNo: 3,
        parameter: 'Financial Proposal and Pricing',
        maximumMark: 30,
        marksScored: null
      },
      {
        tenderID: tenderId,
        vendorID: 'VENDOR001',
        bidID: null,
        serialNo: 4,
        parameter: 'Quality Assurance and Compliance',
        maximumMark: 10,
        marksScored: null
      },
      {
        tenderID: tenderId,
        vendorID: 'VENDOR001',
        bidID: null,
        serialNo: 5,
        parameter: 'Timeline and Delivery Schedule',
        maximumMark: 5,
        marksScored: null
      }
    ];

    // Ensure vendorTenderParticipation structure is complete
    if (!tender.vendorTenderParticipation) {
      tender.vendorTenderParticipation = {
        tenderID: tenderId,
        vendorID: 'VENDOR001',
        firstTimeView: false,
        joiningFee: tender.joiningFee || 0,
        isFeepaid: true,
        action: null
      };
    } else {
      // Ensure the action is set correctly
      tender.vendorTenderParticipation.action = tender.action || tender.vendorTenderParticipation.action || null;
    }

    return {
      isSuccess: true,
      isSystemError: false,
      code: 200,
      data: tender,
      message: 'Tender details retrieved successfully'
    };
  }

  // ==================== Bids ====================
  private getBidsResponse(): any {
    return {
      isSuccess: true,
      isSystemError: false,
      code: 200,
      data: [
        {
          bidID: 'BID001',
          tenderID: 'TND001',
          tenderReferenceNo: 'REF-2024-001',
          tenderDescription: 'Construction of Office Building in Doha',
          status: 'Submitted',
          bidValue: 1500000,
          bidStartDate: '2024-01-15T00:00:00',
          bidClosingDate: '2024-02-15T00:00:00',
          bidValidUntil: '2024-08-15T00:00:00',
          canSubmit: false,
          vendorID: 'VENDOR001',
          submittedDate: '2024-02-10T10:30:00',
          extendedDate: '2024-02-20T00:00:00',
          isNew: false,
          BidID: 'BID001',
          Status: 'Submitted',
          BidStartDate: '2024-01-15T00:00:00',
          BidClosingDate: '2024-02-15T00:00:00',
          ExtendedDate: '2024-02-20T00:00:00',
          tender: {
            tenderID: 'TND001',
            startDate: '2024-01-15T00:00:00',
            closureDate: '2024-02-15T00:00:00',
            extendedDate: '2024-02-20T00:00:00'
          }
        },
        {
          bidID: 'BID002',
          tenderID: 'TND002',
          tenderReferenceNo: 'REF-2024-002',
          tenderDescription: 'IT Infrastructure Upgrade Project',
          status: 'Draft',
          bidValue: 1250000,
          bidStartDate: '2024-02-01T00:00:00',
          bidClosingDate: '2024-03-01T00:00:00',
          bidValidUntil: '2024-09-01T00:00:00',
          canSubmit: true,
          vendorID: 'VENDOR001',
          submittedDate: null,
          extendedDate: '2024-03-05T00:00:00',
          isNew: true,
          BidID: 'BID002',
          Status: 'Draft',
          BidStartDate: '2024-02-01T00:00:00',
          BidClosingDate: '2024-03-01T00:00:00',
          ExtendedDate: '2024-03-05T00:00:00',
          tender: {
            tenderID: 'TND002',
            startDate: '2024-02-01T00:00:00',
            closureDate: '2024-03-01T00:00:00',
            extendedDate: '2024-03-05T00:00:00'
          }
        },
        {
          bidID: 'BID003',
          tenderID: 'TND003',
          tenderReferenceNo: 'REF-2024-003',
          tenderDescription: 'Annual Maintenance Services',
          status: 'Awarded',
          bidValue: 850000,
          bidStartDate: '2024-01-20T00:00:00',
          bidClosingDate: '2024-02-20T00:00:00',
          bidValidUntil: '2024-08-20T00:00:00',
          canSubmit: false,
          vendorID: 'VENDOR001',
          submittedDate: '2024-02-15T14:20:00',
          extendedDate: '2024-02-25T00:00:00',
          isNew: false,
          BidID: 'BID003',
          Status: 'Awarded',
          BidStartDate: '2024-01-20T00:00:00',
          BidClosingDate: '2024-02-20T00:00:00',
          ExtendedDate: '2024-02-25T00:00:00',
          tender: {
            tenderID: 'TND003',
            startDate: '2024-01-20T00:00:00',
            closureDate: '2024-02-20T00:00:00',
            extendedDate: '2024-02-25T00:00:00'
          }
        },
        {
          bidID: 'BID004',
          tenderID: 'TND004',
          tenderReferenceNo: 'REF-2024-004',
          tenderDescription: 'Supply of Office Furniture and Equipment',
          status: 'Submitted',
          bidValue: 1250000,
          bidStartDate: '2024-02-10T00:00:00',
          bidClosingDate: '2024-03-10T00:00:00',
          bidValidUntil: '2024-09-10T00:00:00',
          canSubmit: false,
          vendorID: 'VENDOR001',
          submittedDate: '2024-03-05T09:15:00',
          extendedDate: '2024-03-15T00:00:00',
          isNew: false,
          BidID: 'BID004',
          Status: 'Submitted',
          BidStartDate: '2024-02-10T00:00:00',
          BidClosingDate: '2024-03-10T00:00:00',
          ExtendedDate: '2024-03-15T00:00:00',
          tender: {
            tenderID: 'TND004',
            startDate: '2024-02-10T00:00:00',
            closureDate: '2024-03-10T00:00:00',
            extendedDate: '2024-03-15T00:00:00'
          }
        },
        {
          bidID: 'BID005',
          tenderID: 'TND005',
          tenderReferenceNo: 'REF-2024-005',
          tenderDescription: 'Security Systems Installation',
          status: 'Draft',
          bidValue: 950000,
          bidStartDate: '2024-02-15T00:00:00',
          bidClosingDate: '2024-03-15T00:00:00',
          bidValidUntil: '2024-09-15T00:00:00',
          canSubmit: true,
          vendorID: 'VENDOR001',
          submittedDate: null,
          extendedDate: '2024-03-20T00:00:00',
          isNew: true,
          BidID: 'BID005',
          Status: 'Draft',
          BidStartDate: '2024-02-15T00:00:00',
          BidClosingDate: '2024-03-15T00:00:00',
          ExtendedDate: '2024-03-20T00:00:00',
          tender: {
            tenderID: 'TND005',
            startDate: '2024-02-15T00:00:00',
            closureDate: '2024-03-15T00:00:00',
            extendedDate: '2024-03-20T00:00:00'
          }
        }
      ],
      totalCount: 5
    };
  }

  private getBidDetailsResponse(): any {
    return {
      isSuccess: true,
      data: {
        bidID: 'BID001',
        tenderID: 'TND001',
        status: 'Submitted',
        bidValue: 1500000,
        bidStartDate: '2024-01-15T00:00:00',
        bidClosingDate: '2024-02-15T00:00:00',
        bidValidUntil: '2024-08-15T00:00:00',
        vendorID: 'VENDOR001',
        tender: {
          tenderID: 'TND001',
          referenceNo: 'REF-2024-001',
          description: 'Construction of Office Building in Doha',
          entity: 'Qatar Foundation'
        },
        attachments: []
      }
    };
  }

  // ==================== Purchase Orders ====================
  // Record 1: PO001 -> GRN001 -> INV001 -> PAY001 (Completed Cycle)
  // Record 2: PO002 -> GRN002 -> INV002 -> PAY002 (In Progress)
  // Record 3: PO003 -> GRN003 -> INV003 -> PAY003 (Completed Cycle)
  // Record 4: PO004 -> GRN004 -> INV004 -> PAY004 (Partial Payment)
  // Record 5: PO005 -> GRN005 -> INV005 -> PAY005 (Pending)
  private getPurchaseOrdersResponse(): any {
    return {
      isSuccess: true,
      result: [
        {
          purchId: 'PO001',
          purchName: 'Purchase Order 001',
          purchStatus: 'Confirmed',
          vendorAccount: 'VENDOR001',
          vendorName: 'Mock Vendor Company',
          orderDate: '2024-01-15T00:00:00',
          transDateParsed: new Date('2024-01-15T00:00:00'),
          deliveryDate: '2024-02-15T00:00:00',
          status: 'Confirmed',
          currencyCode: 'QAR',
          totalAmount: 250000,
          poOrderer: 'John Smith',
          deliveryAddress: 'Al Corniche Street, Building 123, Doha',
          isConfirmed: true,
          lines: [
            {
              lineNumber: 1,
              itemNumber: 'ITEM001',
              itemDescription: 'Office Furniture Set',
              quantity: 50,
              unitPrice: 2000,
              lineAmount: 100000,
              postedQuantityValue: 30, // 30 received from GRN001
              quantityReceivable: 20, // 20 remaining
              unit: 'PCS'
            },
            {
              lineNumber: 2,
              itemNumber: 'ITEM002',
              itemDescription: 'Computer Equipment',
              quantity: 100,
              unitPrice: 1500,
              lineAmount: 150000,
              postedQuantityValue: 80, // 80 received from GRN002
              quantityReceivable: 20, // 20 remaining
              unit: 'PCS'
            }
          ]
        },
        {
          purchId: 'PO002',
          purchName: 'Purchase Order 002',
          purchStatus: 'Pending',
          vendorAccount: 'VENDOR001',
          vendorName: 'Mock Vendor Company',
          orderDate: '2024-02-01T00:00:00',
          transDateParsed: new Date('2024-02-01T00:00:00'),
          deliveryDate: '2024-03-01T00:00:00',
          status: 'Pending',
          currencyCode: 'QAR',
          totalAmount: 180000,
          poOrderer: 'Sarah Johnson',
          deliveryAddress: 'West Bay Area, Tower 456, Doha',
          isConfirmed: false,
          lines: [
            {
              lineNumber: 1,
              itemNumber: 'ITEM003',
              itemDescription: 'IT Equipment - Servers & Networking',
              quantity: 75,
              unitPrice: 2400,
              lineAmount: 180000,
              postedQuantityValue: 0, // Not received yet
              quantityReceivable: 75,
              unit: 'PCS'
            }
          ]
        },
        {
          purchId: 'PO003',
          purchName: 'Purchase Order 003',
          purchStatus: 'Completed',
          vendorAccount: 'VENDOR001',
          vendorName: 'Mock Vendor Company',
          orderDate: '2024-01-10T00:00:00',
          transDateParsed: new Date('2024-01-10T00:00:00'),
          deliveryDate: '2024-02-10T00:00:00',
          status: 'Completed',
          currencyCode: 'QAR',
          totalAmount: 320000,
          poOrderer: 'Ahmed Al-Mansouri',
          deliveryAddress: 'Industrial Area, Warehouse 789, Doha',
          isConfirmed: true,
          lines: [
            {
              lineNumber: 1,
              itemNumber: 'ITEM004',
              itemDescription: 'Construction Materials - Cement & Steel',
              quantity: 200,
              unitPrice: 1600,
              lineAmount: 320000,
              postedQuantityValue: 200, // All received from GRN003
              quantityReceivable: 0, // Completed
              unit: 'PCS'
            }
          ]
        },
        {
          purchId: 'PO004',
          purchName: 'Purchase Order 004',
          purchStatus: 'Confirmed',
          vendorAccount: 'VENDOR001',
          vendorName: 'Mock Vendor Company',
          orderDate: '2024-02-10T00:00:00',
          transDateParsed: new Date('2024-02-10T00:00:00'),
          deliveryDate: '2024-03-10T00:00:00',
          status: 'Confirmed',
          currencyCode: 'QAR',
          totalAmount: 450000,
          poOrderer: 'Fatima Al-Zahra',
          deliveryAddress: 'Al Sadd Street, Building 321, Doha',
          isConfirmed: true,
          lines: [
            {
              lineNumber: 1,
              itemNumber: 'ITEM005',
              itemDescription: 'Security Systems - CCTV & Access Control',
              quantity: 120,
              unitPrice: 3750,
              lineAmount: 450000,
              postedQuantityValue: 60, // 60 received from GRN004
              quantityReceivable: 60, // 60 remaining
              unit: 'PCS'
            }
          ]
        },
        {
          purchId: 'PO005',
          purchName: 'Purchase Order 005',
          purchStatus: 'Pending',
          vendorAccount: 'VENDOR001',
          vendorName: 'Mock Vendor Company',
          orderDate: '2024-02-20T00:00:00',
          transDateParsed: new Date('2024-02-20T00:00:00'),
          deliveryDate: '2024-03-20T00:00:00',
          status: 'Pending',
          currencyCode: 'QAR',
          totalAmount: 275000,
          poOrderer: 'Mohammed Al-Thani',
          deliveryAddress: 'Pearl-Qatar, Residential Complex, Doha',
          isConfirmed: false,
          lines: [
            {
              lineNumber: 1,
              itemNumber: 'ITEM006',
              itemDescription: 'Office Supplies - Stationery & Paper',
              quantity: 550,
              unitPrice: 500,
              lineAmount: 275000,
              postedQuantityValue: 0, // Not received yet
              quantityReceivable: 550,
              unit: 'PCS'
            }
          ]
        }
      ],
      totalCount: 5
    };
  }

  // ==================== Invoices ====================
  // Each Invoice is linked to corresponding PO and GRN
  private getInvoicesResponse(): any {
    return {
      isSuccess: true,
      result: {
        invoiceHeaders: [
          {
            invoiceId: 'INV001',
            invoiceNumber: 'INV-2024-001',
            invoiceDate: '2024-02-01T00:00:00',
            invoiceDateParsed: new Date('2024-02-01T00:00:00'),
            invoiceReceivedDate: '2024-02-05T00:00:00',
            invoiceReceivedDateParsed: new Date('2024-02-05T00:00:00'),
            invoiceDescription: 'Invoice for Office Furniture Set (30 PCS) from PO001 - GRN001',
            productReceiptNo: 'GRN001',
            purchId: 'PO001',
            status: 'Posted',
            invoiceAmount: 100000,
            currencyCode: 'QAR',
            recId: 'REC001',
            vendorAccount: 'VENDOR001',
            vendorName: 'Mock Vendor Company',
            expanded: false,
            lines: [
              {
                lineNumber: 1,
                lineDescription: 'Office Furniture Set - 30 PCS received via GRN001',
                quantity: 30,
                unitPrice: 2000,
                purchUnit: 'PCS',
                lineAmount: 60000
              },
              {
                lineNumber: 2,
                lineDescription: 'Computer Equipment - 20 PCS partial delivery',
                quantity: 20,
                unitPrice: 2000,
                purchUnit: 'PCS',
                lineAmount: 40000
              }
            ],
            poHeaderDetails: {
              currencyCode: 'QAR',
              totalAmount: 250000,
              purchId: 'PO001',
              purchName: 'Purchase Order 001 - Office Furniture & Computers'
            }
          },
          {
            invoiceId: 'INV002',
            invoiceNumber: 'INV-2024-002',
            invoiceDate: '2024-02-10T00:00:00',
            invoiceDateParsed: new Date('2024-02-10T00:00:00'),
            invoiceReceivedDate: '2024-02-12T00:00:00',
            invoiceReceivedDateParsed: new Date('2024-02-12T00:00:00'),
            invoiceDescription: 'Invoice for IT Equipment (75 PCS) from PO002 - GRN002',
            productReceiptNo: 'GRN002',
            purchId: 'PO002',
            status: 'Draft',
            invoiceAmount: 180000,
            currencyCode: 'QAR',
            recId: 'REC002',
            vendorAccount: 'VENDOR001',
            vendorName: 'Mock Vendor Company',
            expanded: false,
            lines: [
              {
                lineNumber: 1,
                lineDescription: 'IT Equipment - Servers & Networking - 75 PCS from GRN002',
                quantity: 75,
                unitPrice: 2400,
                purchUnit: 'PCS',
                lineAmount: 180000
              }
            ],
            poHeaderDetails: {
              currencyCode: 'QAR',
              totalAmount: 180000,
              purchId: 'PO002',
              purchName: 'Purchase Order 002 - IT Equipment'
            }
          },
          {
            invoiceId: 'INV003',
            invoiceNumber: 'INV-2024-003',
            invoiceDate: '2024-01-20T00:00:00',
            invoiceDateParsed: new Date('2024-01-20T00:00:00'),
            invoiceReceivedDate: '2024-01-25T00:00:00',
            invoiceReceivedDateParsed: new Date('2024-01-25T00:00:00'),
            invoiceDescription: 'Invoice for Construction Materials (200 PCS) from PO003 - GRN003',
            productReceiptNo: 'GRN003',
            purchId: 'PO003',
            status: 'Posted',
            invoiceAmount: 320000,
            currencyCode: 'QAR',
            recId: 'REC003',
            vendorAccount: 'VENDOR001',
            vendorName: 'Mock Vendor Company',
            expanded: false,
            lines: [
              {
                lineNumber: 1,
                lineDescription: 'Construction Materials - Cement & Steel - 200 PCS from GRN003',
                quantity: 200,
                unitPrice: 1600,
                purchUnit: 'PCS',
                lineAmount: 320000
              }
            ],
            poHeaderDetails: {
              currencyCode: 'QAR',
              totalAmount: 320000,
              purchId: 'PO003',
              purchName: 'Purchase Order 003 - Construction Materials'
            }
          },
          {
            invoiceId: 'INV004',
            invoiceNumber: 'INV-2024-004',
            invoiceDate: '2024-02-15T00:00:00',
            invoiceDateParsed: new Date('2024-02-15T00:00:00'),
            invoiceReceivedDate: '2024-02-18T00:00:00',
            invoiceReceivedDateParsed: new Date('2024-02-18T00:00:00'),
            invoiceDescription: 'Invoice for Security Systems (60 PCS) from PO004 - GRN004',
            productReceiptNo: 'GRN004',
            purchId: 'PO004',
            status: 'Posted',
            invoiceAmount: 225000,
            currencyCode: 'QAR',
            recId: 'REC004',
            vendorAccount: 'VENDOR001',
            vendorName: 'Mock Vendor Company',
            expanded: false,
            lines: [
              {
                lineNumber: 1,
                lineDescription: 'Security Systems - CCTV & Access Control - 60 PCS from GRN004',
                quantity: 60,
                unitPrice: 3750,
                purchUnit: 'PCS',
                lineAmount: 225000
              }
            ],
            poHeaderDetails: {
              currencyCode: 'QAR',
              totalAmount: 450000,
              purchId: 'PO004',
              purchName: 'Purchase Order 004 - Security Systems'
            }
          },
          {
            invoiceId: 'INV005',
            invoiceNumber: 'INV-2024-005',
            invoiceDate: '2024-02-25T00:00:00',
            invoiceDateParsed: new Date('2024-02-25T00:00:00'),
            invoiceReceivedDate: '2024-02-28T00:00:00',
            invoiceReceivedDateParsed: new Date('2024-02-28T00:00:00'),
            invoiceDescription: 'Invoice for Office Supplies (275 PCS) from PO005 - GRN005',
            productReceiptNo: 'GRN005',
            purchId: 'PO005',
            status: 'Draft',
            invoiceAmount: 137500,
            currencyCode: 'QAR',
            recId: null,
            vendorAccount: 'VENDOR001',
            vendorName: 'Mock Vendor Company',
            expanded: false,
            lines: [
              {
                lineNumber: 1,
                lineDescription: 'Office Supplies - Stationery & Paper - 275 PCS from GRN005',
                quantity: 275,
                unitPrice: 500,
                purchUnit: 'PCS',
                lineAmount: 137500
              }
            ],
            poHeaderDetails: {
              currencyCode: 'QAR',
              totalAmount: 275000,
              purchId: 'PO005',
              purchName: 'Purchase Order 005 - Office Supplies'
            }
          }
        ],
        poHeaderDetails: {
          currencyCode: 'QAR',
          totalAmount: 250000,
          purchId: 'PO001',
          purchName: 'Purchase Order 001 - Office Furniture & Computers'
        }
      },
      totalCount: 5
    };
  }

  // ==================== Payments ====================
  // Each Payment is linked to corresponding Invoice and PO
  private getPaymentsResponse(): any {
    return {
      isSuccess: true,
      result: [
        {
          paymentId: 'PAY001',
          paymentNumber: 'PAY-2024-001',
          paymentDate: '2024-02-15T00:00:00',
          paymentDateParsed: new Date('2024-02-15T00:00:00'),
          invoiceId: 'INV001',
          invoiceNumber: 'INV-2024-001',
          amount: 100000,
          paidAmount: 100000,
          currencyCode: 'QAR',
          currency: 'QAR',
          status: 'Paid',
          paymentMethod: 'Bank Transfer',
          vendorAccount: 'VENDOR001',
          vendorName: 'Mock Vendor Company',
          bankBeneficieryName: 'Mock Vendor Company',
          bankIBAN: 'QA12345678901234567890',
          bankAccountNumber: '1234567890',
          bankName: 'Qatar National Bank',
          expanded: false,
          lines: [
            {
              purchId: 'PO001',
              voucher: 'VCH-2024-001',
              invoiceId: 'INV001',
              invoiceNumber: 'INV-2024-001',
              currencyCode: 'QAR',
              amountDebit: 100000,
              amountCredit: 0
            }
          ]
        },
        {
          paymentId: 'PAY002',
          paymentNumber: 'PAY-2024-002',
          paymentDate: '2024-02-20T00:00:00',
          paymentDateParsed: new Date('2024-02-20T00:00:00'),
          invoiceId: 'INV002',
          invoiceNumber: 'INV-2024-002',
          amount: 180000,
          paidAmount: 180000,
          currencyCode: 'QAR',
          currency: 'QAR',
          status: 'Pending',
          paymentMethod: 'Cheque',
          vendorAccount: 'VENDOR001',
          vendorName: 'Mock Vendor Company',
          bankBeneficieryName: 'Mock Vendor Company',
          bankIBAN: 'QA12345678901234567890',
          bankAccountNumber: '1234567890',
          bankName: 'Qatar National Bank',
          expanded: false,
          lines: [
            {
              purchId: 'PO002',
              voucher: 'VCH-2024-002',
              invoiceId: 'INV002',
              invoiceNumber: 'INV-2024-002',
              currencyCode: 'QAR',
              amountDebit: 180000,
              amountCredit: 0
            }
          ]
        },
        {
          paymentId: 'PAY003',
          paymentNumber: 'PAY-2024-003',
          paymentDate: '2024-02-25T00:00:00',
          paymentDateParsed: new Date('2024-02-25T00:00:00'),
          invoiceId: 'INV003',
          invoiceNumber: 'INV-2024-003',
          amount: 320000,
          paidAmount: 320000,
          currencyCode: 'QAR',
          currency: 'QAR',
          status: 'Paid',
          paymentMethod: 'Bank Transfer',
          vendorAccount: 'VENDOR001',
          vendorName: 'Mock Vendor Company',
          bankBeneficieryName: 'Mock Vendor Company',
          bankIBAN: 'QA12345678901234567890',
          bankAccountNumber: '1234567890',
          bankName: 'Qatar National Bank',
          expanded: false,
          lines: [
            {
              purchId: 'PO003',
              voucher: 'VCH-2024-003',
              invoiceId: 'INV003',
              invoiceNumber: 'INV-2024-003',
              currencyCode: 'QAR',
              amountDebit: 320000,
              amountCredit: 0
            }
          ]
        },
        {
          paymentId: 'PAY004',
          paymentNumber: 'PAY-2024-004',
          paymentDate: '2024-03-01T00:00:00',
          paymentDateParsed: new Date('2024-03-01T00:00:00'),
          invoiceId: 'INV004',
          invoiceNumber: 'INV-2024-004',
          amount: 225000,
          paidAmount: 225000,
          currencyCode: 'QAR',
          currency: 'QAR',
          status: 'Pending',
          paymentMethod: 'Cheque',
          vendorAccount: 'VENDOR001',
          vendorName: 'Mock Vendor Company',
          bankBeneficieryName: 'Mock Vendor Company',
          bankIBAN: 'QA12345678901234567890',
          bankAccountNumber: '1234567890',
          bankName: 'Qatar National Bank',
          expanded: false,
          lines: [
            {
              purchId: 'PO004',
              voucher: 'VCH-2024-004',
              invoiceId: 'INV004',
              invoiceNumber: 'INV-2024-004',
              currencyCode: 'QAR',
              amountDebit: 225000,
              amountCredit: 0
            }
          ]
        },
        {
          paymentId: 'PAY005',
          paymentNumber: 'PAY-2024-005',
          paymentDate: '2024-03-05T00:00:00',
          paymentDateParsed: new Date('2024-03-05T00:00:00'),
          invoiceId: 'INV005',
          invoiceNumber: 'INV-2024-005',
          amount: 137500,
          paidAmount: 137500,
          currencyCode: 'QAR',
          currency: 'QAR',
          status: 'Pending',
          paymentMethod: 'Bank Transfer',
          vendorAccount: 'VENDOR001',
          vendorName: 'Mock Vendor Company',
          bankBeneficieryName: 'Mock Vendor Company',
          bankIBAN: 'QA12345678901234567890',
          bankAccountNumber: '1234567890',
          bankName: 'Qatar National Bank',
          expanded: false,
          lines: [
            {
              purchId: 'PO005',
              voucher: 'VCH-2024-005',
              invoiceId: 'INV005',
              invoiceNumber: 'INV-2024-005',
              currencyCode: 'QAR',
              amountDebit: 137500,
              amountCredit: 0
            }
          ]
        }
      ],
      totalCount: 5
    };
  }

  // ==================== GRNs ====================
  // Each GRN is linked to corresponding PO and Invoice
  // isExists = true if Invoice already exists
  private getGRNsResponse(): any {
    return {
      isSuccess: true,
      result: [
        {
          grnId: 'GRN001',
          grnNumber: 'GRN-2024-001',
          grnDate: '2024-02-01T00:00:00',
          productReceiptNo: 'GRN001',
          productReceiptDateParsed: new Date('2024-02-01T00:00:00'),
          purchId: 'PO001',
          status: 'Posted',
          totalQuantity: 30,
          isExists: false, // Invoice INV001 exists but new invoice can be created
          expanded: false,
          lines: [
            {
              lineNumber: 1,
              itemNumber: 'ITEM001',
              itemDescription: 'Office Furniture Set',
              lineDescription: 'Office Furniture Set - 30 PCS received from PO001 Line 1',
              quantity: 30,
              receivedQuantity: 30,
              orderedQuantity: 50, // From PO001 Line 1
              unitPrice: 2000,
              unit: 'PCS',
              postedQuantityValue: 30,
              quantityReceivable: 0
            }
          ]
        },
        {
          grnId: 'GRN002',
          grnNumber: 'GRN-2024-002',
          grnDate: '2024-02-10T00:00:00',
          productReceiptNo: 'GRN002',
          productReceiptDateParsed: new Date('2024-02-10T00:00:00'),
          purchId: 'PO002',
          status: 'Draft',
          totalQuantity: 75,
          isExists: false, // Invoice INV002 exists but in Draft status
          expanded: false,
          lines: [
            {
              lineNumber: 1,
              itemNumber: 'ITEM003',
              itemDescription: 'IT Equipment - Servers & Networking',
              lineDescription: 'IT Equipment - Servers & Networking - 75 PCS received from PO002 Line 1',
              quantity: 75,
              receivedQuantity: 75,
              orderedQuantity: 75, // From PO002 Line 1
              unitPrice: 2400,
              unit: 'PCS',
              postedQuantityValue: 0,
              quantityReceivable: 0
            }
          ]
        },
        {
          grnId: 'GRN003',
          grnNumber: 'GRN-2024-003',
          grnDate: '2024-01-25T00:00:00',
          productReceiptNo: 'GRN003',
          productReceiptDateParsed: new Date('2024-01-25T00:00:00'),
          purchId: 'PO003',
          status: 'Posted',
          totalQuantity: 200,
          isExists: true, // Invoice INV003 exists and Posted
          expanded: false,
          lines: [
            {
              lineNumber: 1,
              itemNumber: 'ITEM004',
              itemDescription: 'Construction Materials - Cement & Steel',
              lineDescription: 'Construction Materials - Cement & Steel - 200 PCS received from PO003 Line 1',
              quantity: 200,
              receivedQuantity: 200,
              orderedQuantity: 200, // From PO003 Line 1
              unitPrice: 1600,
              unit: 'PCS',
              postedQuantityValue: 200,
              quantityReceivable: 0
            }
          ]
        },
        {
          grnId: 'GRN004',
          grnNumber: 'GRN-2024-004',
          grnDate: '2024-02-15T00:00:00',
          productReceiptNo: 'GRN004',
          productReceiptDateParsed: new Date('2024-02-15T00:00:00'),
          purchId: 'PO004',
          status: 'Posted',
          totalQuantity: 60,
          isExists: false, // Invoice INV004 exists but new invoice can be created for remaining quantity
          expanded: false,
          lines: [
            {
              lineNumber: 1,
              itemNumber: 'ITEM005',
              itemDescription: 'Security Systems - CCTV & Access Control',
              lineDescription: 'Security Systems - CCTV & Access Control - 60 PCS received from PO004 Line 1',
              quantity: 60,
              receivedQuantity: 60,
              orderedQuantity: 120, // From PO004 Line 1 (60 remaining)
              unitPrice: 3750,
              unit: 'PCS',
              postedQuantityValue: 60,
              quantityReceivable: 0
            }
          ]
        },
        {
          grnId: 'GRN005',
          grnNumber: 'GRN-2024-005',
          grnDate: '2024-02-25T00:00:00',
          productReceiptNo: 'GRN005',
          productReceiptDateParsed: new Date('2024-02-25T00:00:00'),
          purchId: 'PO005',
          status: 'Draft',
          totalQuantity: 275,
          isExists: false, // Invoice INV005 exists but in Draft status
          expanded: false,
          lines: [
            {
              lineNumber: 1,
              itemNumber: 'ITEM006',
              itemDescription: 'Office Supplies - Stationery & Paper',
              lineDescription: 'Office Supplies - Stationery & Paper - 275 PCS received from PO005 Line 1',
              quantity: 275,
              receivedQuantity: 275,
              orderedQuantity: 550, // From PO005 Line 1 (275 remaining)
              unitPrice: 500,
              unit: 'PCS',
              postedQuantityValue: 0,
              quantityReceivable: 275
            }
          ]
        }
      ],
      totalCount: 5
    };
  }

  // ==================== Messages ====================
  private getMessagesResponse(): any {
    return {
      isSuccess: true,
      data: [
        {
          messageId: 'MSG001',
          messageContent: 'Please provide additional documentation for invoice INV-2024-001',
          direction: 'incoming',
          moudle: 'financials',
          referenceNumber: 'INV-2024-001',
          subReferenceNumber: 'PO001',
          vendorID: 'VENDOR001',
          createdAt: '2024-02-05T10:30:00',
          CreatedAt: '2024-02-05T10:30:00'
        },
        {
          messageId: 'MSG002',
          messageContent: 'We have submitted the requested documents',
          direction: 'outgoing',
          moudle: 'financials',
          referenceNumber: 'INV-2024-001',
          subReferenceNumber: 'PO001',
          vendorID: 'VENDOR001',
          createdAt: '2024-02-05T14:20:00',
          CreatedAt: '2024-02-05T14:20:00'
        },
        {
          messageId: 'MSG003',
          messageContent: 'Your bid has been reviewed and approved',
          direction: 'incoming',
          moudle: 'bidding',
          referenceNumber: 'BID001',
          subReferenceNumber: 'TND001',
          vendorID: 'VENDOR001',
          createdAt: '2024-02-10T09:15:00',
          CreatedAt: '2024-02-10T09:15:00'
        },
        {
          messageId: 'MSG004',
          messageContent: 'General inquiry about tender process',
          direction: 'incoming',
          moudle: 'general',
          referenceNumber: 'TND002',
          subReferenceNumber: null,
          vendorID: 'VENDOR001',
          createdAt: '2024-02-12T11:45:00',
          CreatedAt: '2024-02-12T11:45:00'
        },
        {
          messageId: 'MSG005',
          messageContent: 'Payment confirmation for invoice INV-2024-003',
          direction: 'incoming',
          moudle: 'financials',
          referenceNumber: 'INV-2024-003',
          subReferenceNumber: 'PO003',
          vendorID: 'VENDOR001',
          createdAt: '2024-02-15T16:30:00',
          CreatedAt: '2024-02-15T16:30:00'
        }
      ],
      totalCount: 5
    };
  }

  // ==================== Notifications ====================
  private getNotificationsResponse(): any {
    return {
      isSuccess: true,
      result: [
        {
          notificationId: 'NOTIF001',
          title: 'New Tender Available',
          message: 'A new tender has been published: REF-2024-001',
          type: 'tender',
          isRead: false,
          createdAt: '2024-01-10T08:00:00',
          link: '/tender/tenderdetails/TND001'
        },
        {
          notificationId: 'NOTIF002',
          title: 'Payment Received',
          message: 'Payment PAY-2024-001 has been processed',
          type: 'payment',
          isRead: false,
          createdAt: '2024-02-15T10:30:00',
          link: '/financials'
        },
        {
          notificationId: 'NOTIF003',
          title: 'Bid Status Updated',
          message: 'Your bid BID001 status has been updated',
          type: 'bid',
          isRead: true,
          createdAt: '2024-02-10T09:15:00',
          link: '/bid/biddetails/BID001'
        },
        {
          notificationId: 'NOTIF004',
          title: 'Invoice Posted',
          message: 'Invoice INV-2024-001 has been posted',
          type: 'invoice',
          isRead: true,
          createdAt: '2024-02-01T14:20:00',
          link: '/financials'
        },
        {
          notificationId: 'NOTIF005',
          title: 'GRN Received',
          message: 'GRN GRN-2024-004 has been received for PO004',
          type: 'grn',
          isRead: false,
          createdAt: '2024-02-15T11:00:00',
          link: '/financials'
        }
      ],
      totalCount: 5
    };
  }

  // ==================== Vendor Profile ====================
  private getVendorProfileResponse(): any {
    return {
      isSuccess: true,
      isSystemError: false,
      code: 200,
      data: {
        vendorID: 'VENDOR001',
        erpVendorID: 'ERP001',
        vendorName: 'Mock Vendor Company',
        vendorReference: 'VREF-001',
        email: 'vendor@example.com',
        financeEmail: 'finance@example.com',
        mobile: '+974 1234 5678',
        status: 'Draft',
        vendorType: 'Organization',
        localOrForeign: 'Local',
        country: 'Qatar',
        currency: 'QAR',
        registeredDate: '2020-01-15T00:00:00',
        registrationDate: '2020-01-15T00:00:00',
        addressDescriptionPrimary: 'Main Office',
        streetPrimary: 'Al Corniche Street, Building 123',
        streetSecondary: 'Secondary Branch Location',
        cityPrimary: 'Doha',
        citySecondary: 'Al Rayyan',
        countryRegionIdPrimary: 'QA',
        countryRegionIdSecondary: 'QA',
        zipCodePrimary: '12345',
        zipCodeSecondary: '54321',
        phonePrimary: '+974 1234 5678',
        phoneSecondary: '+974 9876 5432',
        website: 'https://www.mockvendor.com',
        methodOfPayment: 'BT',
        termsOfPayment: '30 Days',
        workingCapital: '5000000',
        noOfYears: '10',
        noOfEmployees: '50',
        numberofBranches: 2,
        organizationalstructure: 'Limited Liability Company',
        category: [
          { categoryID: 'CAT001', CategoryID: 'CAT001', id: '1', createdAt: '2024-01-01', createdBy: 'admin', modifiedAt: '2024-01-01', modifiedBy: 'admin' },
          { categoryID: 'CAT002', CategoryID: 'CAT002', id: '2', createdAt: '2024-01-01', createdBy: 'admin', modifiedAt: '2024-01-01', modifiedBy: 'admin' }
        ],
        subCategory: [
          { subCategoryID: 'SUBCAT001', SubCategoryID: 'SUBCAT001', id: '1', createdAt: '2024-01-01', createdBy: 'admin', modifiedAt: '2024-01-01', modifiedBy: 'admin' },
          { subCategoryID: 'SUBCAT002', SubCategoryID: 'SUBCAT002', id: '2', createdAt: '2024-01-01', createdBy: 'admin', modifiedAt: '2024-01-01', modifiedBy: 'admin' }
        ],
        contactPerson: [
          {
            FirstName: 'John',
            LastName: 'Doe',
            Title: 'CEO',
            Email: 'john.doe@example.com',
            LandlineNumber: '+974 1234 5678',
            MobileNumber: '+974 5555 1234'
          }
        ],
        identificationDetails: [],
        associatedCompanies: [],
        managementTeam: [],
        attachments: [],
        bankAccount: {
          name: 'Qatar National Bank',
          accountNumber: '1234567890',
          bankIBAN: 'QA12345678901234567890',
          swift: 'QNBAQAQA',
          branch: 'Doha Branch',
          beneficiaryName: 'Mock Vendor Company',
          email: 'bank@example.com',
          address: 'Bank Address, Doha',
          currencyCode: 'QAR',
          activeDate: '2024-01-15',
          bankCountry: 'Qatar',
          documents: []
        }
      }
    };
  }

  // ==================== Bank Accounts ====================
  private getBankAccountsResponse(): any {
    const base64Sample = 'data:application/pdf;base64,JVBERi0xLjQKJeLjz9MKMyAwIG9iago8PAovVHlwZSAvQ2F0YWxvZwovUGFnZXMgMSAwIFIKPj4KZW5kb2JqCjQgMCBvYmoKPDwKL1R5cGUgL091dGxpbmVzCi9PdXRsaW5lcyAxIDAgUgo+PgplbmRvYmoKMSAwIG9iago8PAovVHlwZSAvUGFnZXMKL0tpZHMgWzIgMCBSXQovQ291bnQgMQo+PgplbmRvYmoKMiAwIG9iago8PAovVHlwZSAvUGFnZQovUGFyZW50IDEgMCBSCi9SZXNvdXJjZXMgPDwKL0ZvbnQgPDwKL0YxIDMgMCBSIAo+Pgo+PgovTWVkaWFCb3ggWzAgMCA2MTIgNzkyXQovQ29udGVudHMgNSAwIFIKPj4KZW5kb2JqCjMgMCBvYmoKPDwKL1R5cGUgL0ZvbnQKL1N1YnR5cGUgL1R5cGUxCi9CYXNlRm9udCAvSGVsdmV0aWNhCj4+CmVuZG9iago1IDAgb2JqCjw8Ci9MZW5ndGggNDQKPj4Kc3RyZWFtCkJUCi9GMSAxMiBUZgooVGhpcyBpcyBhIHNhbXBsZSBQRkYgZmlsZSkKZW5kc3RyZWFtCmVuZG9iagp4cmVmCjAgNgowMDAwMDAwMDAwIDY1NTM1IGYgCjAwMDAwMDAwMDkgMDAwMDAgbiAKMDAwMDAwMDA1NCAwMDAwMCBuIAowMDAwMDAwMTEwIDAwMDAwIG4gCjAwMDAwMDAxNjIgMDAwMDAgbiAKMDAwMDAwMDI0OCAwMDAwMCBuIAp0cmFpbGVyCjw8Ci9TaXplIDYKL1Jvb3QgNCAwIFIKPj4Kc3RhcnR4cmVmCjMxMgolJUVPRgo=';

    return {
      isSuccess: true,
      isSystemError: false,
      code: 200,
      data: [
        {
          id: 1,
          bankID: '1',
          name: '1', // Qatar National Bank ID from lookup
          forignBankName: '',
          bankType: 1,
          bankIBAN: 'QA12345678901234567890',
          swift: 'QNBAQAQA', // Component uses 'swift'
          swiftCode: 'QNBAQAQA', // Model property
          branch: 'Doha Main Branch', // Component uses 'branch'
          branchName: 'Doha Main Branch', // Model property
          beneficiaryName: 'Mock Vendor Company', // Component uses 'beneficiaryName'
          benficeryName: 'Mock Vendor Company', // Model property
          accountNumber: '1234567890',
          email: 'bank@qnb.com.qa',
          address: 'Al Corniche Street, Building 123, Doha, Qatar',
          currencyCode: 'QAR', // Component uses 'currencyCode'
          currency: 'QAR', // Model property
          bankCountry: 'QA',
          bankStatus: 1, // Active
          activeDate: '2024-01-15T00:00:00',
          activeFrom: new Date('2024-01-15T00:00:00'),
          ActiveDate: new Date('2024-01-15T00:00:00'),
          expiryDate: '2025-01-15T00:00:00',
          ExpiryDate: new Date('2025-01-15T00:00:00'),
          countryID: 1,
          vendorID: 1,
          addedOn: new Date('2024-01-15T00:00:00'),
          lastModification: new Date('2024-01-15T00:00:00'),
          bankDocument: '',
          sortCode: '',
          otherBankName: null,
          document: [ // Component uses 'document'
            {
              attachmentId: 'DOC001',
              fileName: 'Bank Account Statement.pdf',
              fileContent: base64Sample,
              filePath: '/documents/bank/BANK001/statement.pdf',
              mimeType: 'application/pdf',
              expiryDate: new Date('2025-01-15T00:00:00')
            },
            {
              attachmentId: 'DOC002',
              fileName: 'Bank Authorization Letter.pdf',
              fileContent: base64Sample,
              filePath: '/documents/bank/BANK001/authorization.pdf',
              mimeType: 'application/pdf',
              expiryDate: new Date('2025-01-15T00:00:00')
            }
          ],
          TheDocuments: [ // Model property
            {
              attachmentId: 'DOC001',
              fileName: 'Bank Account Statement.pdf',
              fileContent: base64Sample,
              filePath: '/documents/bank/BANK001/statement.pdf',
              expiryDate: new Date('2025-01-15T00:00:00')
            },
            {
              attachmentId: 'DOC002',
              fileName: 'Bank Authorization Letter.pdf',
              fileContent: base64Sample,
              filePath: '/documents/bank/BANK001/authorization.pdf',
              expiryDate: new Date('2025-01-15T00:00:00')
            }
          ]
        },
        {
          id: 2,
          bankID: '2',
          name: '2', // Commercial Bank ID from lookup
          forignBankName: '',
          bankType: 1,
          bankIBAN: 'QA98765432109876543210',
          swift: 'CBQAQAQA',
          swiftCode: 'CBQAQAQA',
          branch: 'West Bay Branch',
          branchName: 'West Bay Branch',
          beneficiaryName: 'Mock Vendor Company',
          benficeryName: 'Mock Vendor Company',
          accountNumber: '9876543210',
          email: 'bank@cbbank.com.qa',
          address: 'West Bay Area, Tower 456, Doha, Qatar',
          currencyCode: 'QAR',
          currency: 'QAR',
          bankCountry: 'QA',
          bankStatus: 1, // Active
          activeDate: '2024-02-01T00:00:00',
          activeFrom: new Date('2024-02-01T00:00:00'),
          ActiveDate: new Date('2024-02-01T00:00:00'),
          expiryDate: '2025-02-01T00:00:00',
          ExpiryDate: new Date('2025-02-01T00:00:00'),
          countryID: 1,
          vendorID: 1,
          addedOn: new Date('2024-02-01T00:00:00'),
          lastModification: new Date('2024-02-01T00:00:00'),
          bankDocument: '',
          sortCode: '',
          otherBankName: null,
          document: [
            {
              attachmentId: 'DOC003',
              fileName: 'Commercial Bank Account Details.pdf',
              fileContent: base64Sample,
              filePath: '/documents/bank/BANK002/details.pdf',
              mimeType: 'application/pdf',
              expiryDate: new Date('2025-02-01T00:00:00')
            }
          ],
          TheDocuments: [
            {
              attachmentId: 'DOC003',
              fileName: 'Commercial Bank Account Details.pdf',
              fileContent: base64Sample,
              filePath: '/documents/bank/BANK002/details.pdf',
              expiryDate: new Date('2025-02-01T00:00:00')
            }
          ]
        },
        {
          id: 3,
          bankID: 'other',
          name: 'other', // Other bank option
          forignBankName: '',
          bankType: 2,
          bankIBAN: 'AE123456789012345678901234',
          swift: 'ADCBUAEAA',
          swiftCode: 'ADCBUAEAA',
          branch: 'Dubai Main Branch',
          branchName: 'Dubai Main Branch',
          beneficiaryName: 'Mock Vendor Company',
          benficeryName: 'Mock Vendor Company',
          accountNumber: '5555555555',
          email: 'bank@adcb.com',
          address: 'Sheikh Zayed Road, Dubai, UAE',
          currencyCode: 'AED',
          currency: 'AED',
          bankCountry: 'AE',
          bankStatus: 0, // Pending/Inactive
          activeDate: '2024-03-01T00:00:00',
          activeFrom: new Date('2024-03-01T00:00:00'),
          ActiveDate: new Date('2024-03-01T00:00:00'),
          expiryDate: null,
          ExpiryDate: null,
          countryID: 2,
          vendorID: 1,
          addedOn: new Date('2024-03-01T00:00:00'),
          lastModification: new Date('2024-03-01T00:00:00'),
          bankDocument: '',
          sortCode: '',
          otherBankName: 'Commercial Bank', // Required when name is 'other'
          document: [
            {
              attachmentId: 'DOC004',
              fileName: 'ADCB Account Certificate.pdf',
              fileContent: base64Sample,
              filePath: '/documents/bank/BANK003/certificate.pdf',
              mimeType: 'application/pdf',
              expiryDate: null
            }
          ],
          TheDocuments: [
            {
              attachmentId: 'DOC004',
              fileName: 'ADCB Account Certificate.pdf',
              fileContent: base64Sample,
              filePath: '/documents/bank/BANK003/certificate.pdf',
              expiryDate: null
            }
          ]
        }
      ],
      totalCount: 3
    };
  }

  // ==================== Lookups ====================
  private getLookupsResponse(): any {
    return {
      isSuccess: true,
      countries: [
        { countryId: 'QA', countryName: 'Qatar', countryRegionId: 'QA' },
        { countryId: 'SA', countryName: 'Saudi Arabia', countryRegionId: 'SA' },
        { countryId: 'AE', countryName: 'United Arab Emirates', countryRegionId: 'AE' }
      ],
      currencies: [
        { currencyId: 'QAR', description: 'Qatari Riyal' },
        { currencyId: 'USD', description: 'US Dollar' },
        { currencyId: 'EUR', description: 'Euro' }
      ],
      methodsOfPayment: [
        { id: '1', name: 'Bank Transfer' },
        { id: '2', name: 'Cheque' },
        { id: '3', name: 'Credit Card' }
      ],
      categories: [
        { categoryId: 'CAT001', categoryName: 'Construction' },
        { categoryId: 'CAT002', categoryName: 'IT Services' },
        { categoryId: 'CAT003', categoryName: 'Maintenance' }
      ],
      subCategories: [
        { subCategoryId: 'SUBCAT001', subCategoryName: 'Building Construction', categoryId: 'CAT001' },
        { subCategoryId: 'SUBCAT002', subCategoryName: 'Software Development', categoryId: 'CAT002' }
      ],
      termsOfPayment: [
        { id: '1', name: 'Net 30' },
        { id: '2', name: 'Net 60' },
        { id: '3', name: 'Immediate' }
      ],
      preQualifications: [
        { id: '1', name: 'ISO Certified' },
        { id: '2', name: 'Bidaya Registered' }
      ],
      banks: [
        { bankId: '1', bankName: 'Qatar National Bank' },
        { bankId: '2', bankName: 'Commercial Bank' },
        { bankId: '3', bankName: 'Doha Bank' }
      ],
      documentsMaster: [
        { documentId: 'DOC001', documentName: 'Commercial Registration', parmDocumentFor: 'Vendor', processName: 'Registration' },
        { documentId: 'DOC002', documentName: 'Tax Certificate', parmDocumentFor: 'Vendor', processName: 'Registration' }
      ]
    };
  }

  // ==================== Dashboard ====================
  private getDashboardDataResponse(): any {
    return {
      isSuccess: true,
      data: {
        vendor: {
          vendorID: 'VENDOR001',
          vendorName: 'Mock Vendor Company',
          status: 'Approved',
          applicationSubmissionDate: '2024-01-15T00:00:00',
          lastApplicationModifyDate: '2024-02-01T00:00:00'
        },
        tender: {
          publicCount: 2,
          privateCount: 1,
          latestFiveTenders: [
            {
              tenderID: 'TND001',
              referenceNo: 'REF-2024-001',
              description: 'Construction of Office Building in Doha',
              startDate: '2024-01-15T00:00:00',
              closureDate: '2024-02-15T00:00:00',
              status: 'Open',
              typeofTender: 'Public'
            },
            {
              tenderID: 'TND002',
              referenceNo: 'REF-2024-002',
              description: 'IT Infrastructure Upgrade Project',
              startDate: '2024-02-01T00:00:00',
              closureDate: '2024-03-01T00:00:00',
              status: 'Open',
              typeofTender: 'Public'
            },
            {
              tenderID: 'TND003',
              referenceNo: 'REF-2024-003',
              description: 'Annual Maintenance Services',
              startDate: '2024-01-20T00:00:00',
              closureDate: '2024-02-20T00:00:00',
              status: 'Open',
              typeofTender: 'Private'
            }
          ]
        },
        bid: {
          open: 2,
          submitted: 1
        },
        totalTenders: 15,
        activeTenders: 8,
        submittedBids: 12,
        awardedBids: 3,
        pendingPurchaseOrders: 5,
        totalInvoices: 25,
        pendingInvoices: 3,
        totalPayments: 18,
        pendingPayments: 2,
        unreadMessages: 5,
        unreadNotifications: 8
      }
    };
  }

  // ==================== Documents ====================
  private getDocumentsResponse(): any {
    return {
      isSuccess: true,
      result: [
        {
          documentId: 'DOC001',
          documentName: 'Commercial Registration',
          documentType: 'CR',
          uploadedDate: '2024-01-15T00:00:00',
          status: 'Approved',
          fileSize: 1024000,
          fileName: 'commercial_registration.pdf'
        },
        {
          documentId: 'DOC002',
          documentName: 'Tax Certificate',
          documentType: 'Tax',
          uploadedDate: '2024-01-20T00:00:00',
          status: 'Pending',
          fileSize: 512000,
          fileName: 'tax_certificate.pdf'
        }
      ],
      totalCount: 2
    };
  }

  // ==================== Submit Operations ====================
  private getSubmitTenderResponse(body?: any): any {
    return {
      isSuccess: true,
      data: {
        tenderID: 'TND' + Math.floor(Math.random() * 1000),
        referenceNo: 'REF-' + new Date().getFullYear() + '-' + Math.floor(Math.random() * 1000),
        submittedAt: new Date().toISOString(),
        status: 'Submitted'
      },
      message: 'Tender submitted successfully'
    };
  }

  private getSubmitBidResponse(body?: any): any {
    return {
      isSuccess: true,
      data: {
        bidID: 'BID' + Math.floor(Math.random() * 1000),
        tenderID: body?.tenderID || 'TND001',
        status: 'Submitted',
        submittedAt: new Date().toISOString(),
        bidValue: body?.bidValue || null
      },
      message: 'Bid submitted successfully'
    };
  }

  private getConfirmPOResponse(): any {
    return {
      isSuccess: true,
      data: {
        confirmed: true,
        confirmedAt: new Date().toISOString()
      },
      message: 'Purchase Order confirmed successfully'
    };
  }

  private getCreateInvoiceResponse(body?: any): any {
    return {
      isSuccess: true,
      data: {
        invoiceId: 'INV' + Math.floor(Math.random() * 1000),
        invoiceNumber: body?.invoiceNumber || 'INV-' + new Date().getFullYear() + '-' + Math.floor(Math.random() * 1000),
        status: 'Draft',
        createdAt: new Date().toISOString()
      },
      message: 'Invoice created successfully'
    };
  }

  private getRequestGRNResponse(body?: any): any {
    return {
      isSuccess: true,
      data: {
        grnId: 'GRN' + Math.floor(Math.random() * 1000),
        grnNumber: 'GRN-' + new Date().getFullYear() + '-' + Math.floor(Math.random() * 1000),
        status: 'Draft',
        requestedAt: new Date().toISOString()
      },
      message: 'GRN requested successfully'
    };
  }

  private getSendMessageResponse(body?: any): any {
    return {
      isSuccess: true,
      data: {
        messageId: 'MSG' + Math.floor(Math.random() * 1000),
        sentAt: new Date().toISOString()
      },
      message: 'Message sent successfully'
    };
  }

  private getRegisterVendorResponse(body?: any): any {
    return {
      isSuccess: true,
      data: {
        vendorID: 'VENDOR' + Math.floor(Math.random() * 1000),
        vendorReference: 'VREF-' + Math.floor(Math.random() * 1000),
        registeredAt: new Date().toISOString(),
        status: 'Pending Approval'
      },
      message: 'Vendor registration submitted successfully'
    };
  }

  private getUpdateVendorResponse(body?: any): any {
    return {
      isSuccess: true,
      data: {
        vendorID: body?.vendorID || 'VENDOR001',
        updatedAt: new Date().toISOString()
      },
      message: 'Vendor profile updated successfully'
    };
  }

  private getSubmitBankAccountResponse(body?: any): any {
    return {
      isSuccess: true,
      data: {
        bankAccountId: 'BANK' + Math.floor(Math.random() * 1000),
        submittedAt: new Date().toISOString(),
        status: 'Pending Approval'
      },
      message: 'Bank account submitted successfully'
    };
  }

  private getUpdateBankAccountResponse(body?: any): any {
    return {
      isSuccess: true,
      data: {
        bankAccountId: body?.bankAccountId || 'BANK001',
        updatedAt: new Date().toISOString()
      },
      message: 'Bank account updated successfully'
    };
  }

  private getUploadDocumentsResponse(body?: any): any {
    return {
      isSuccess: true,
      data: {
        documentIds: [
          'DOC' + Math.floor(Math.random() * 1000),
          'DOC' + Math.floor(Math.random() * 1000)
        ],
        uploadedAt: new Date().toISOString()
      },
      message: 'Documents uploaded successfully'
    };
  }

  // ==================== Default Response ====================
  private getDefaultResponse(method: string): any {
    const baseResponse: any = {
      isSuccess: true,
      isSystemError: false,
      code: 200,
      data: null,
      message: 'Mock response - Backend disabled'
    };

    if (method === 'POST' || method === 'PUT' || method === 'PATCH') {
      baseResponse.data = {
        id: Math.floor(Math.random() * 1000),
        createdAt: new Date().toISOString()
      };
    }

    if (method === 'DELETE') {
      baseResponse.deleted = true;
    }

    return baseResponse;
  }
}

